import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Form, Button, Alert, Row, Col, Modal, Dropdown, Accordion, Badge, Spinner } from 'react-bootstrap';
import { FaFileAlt, FaDownload, FaExclamationTriangle, FaServer, FaSearch, FaCheckSquare, FaRegSquare, FaRocket, FaCog, FaChartBar, FaShieldAlt, FaClock, FaNetworkWired, FaDesktop, FaLaptop, FaExclamationCircle, FaCheckCircle, FaTimesCircle, FaCalendarAlt, FaPalette, FaImage } from 'react-icons/fa';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const filterHierarchyByProvider = (hierarchy, selectedProviderId) => {
  if (!hierarchy) return null;
  if (!selectedProviderId) {
    return {
      accounts: [],
      sites: [],
      groups: [],
    };
  }

  return {
    accounts: (hierarchy.accounts || []).filter((account) => account.provider_id === selectedProviderId),
    sites: (hierarchy.sites || []).filter((site) => site.provider_id === selectedProviderId),
    groups: (hierarchy.groups || []).filter((group) => group.provider_id === selectedProviderId),
  };
};

const getHierarchySlices = (hierarchy, selectedAccountId, selectedSiteIds = []) => {
  const accounts = hierarchy?.accounts || [];
  const sites = selectedAccountId
    ? (hierarchy?.sites || []).filter((site) => site.account_id === selectedAccountId)
    : hierarchy?.sites || [];
  const groups = (hierarchy?.groups || []).filter((group) => {
    if (selectedSiteIds.length > 0) return selectedSiteIds.includes(group.site_id);
    if (selectedAccountId) return group.account_id === selectedAccountId;
    return true;
  });

  return { accounts, sites, groups };
};

const getPeriodStart = (period) => {
  const now = new Date();
  switch (period) {
    case 'hourly':
      return new Date(now.getTime() - (60 * 60 * 1000));
    case 'weekly':
      return new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    case 'monthly':
      return new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
    case 'yearly':
      return new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
    case 'all_time':
      return new Date(0);
    case 'daily':
    default:
      return new Date(now.getTime() - (24 * 60 * 60 * 1000));
  }
};

const getScopePeriodLabel = (scope) => {
  if (!scope) return '';
  if (scope.startDate && scope.endDate) {
    return `${scope.startDate} to ${scope.endDate}`;
  }
  return scope.period || '';
};

const buildScopeSummary = ({ accountName, siteNames = [], groupNames = [], scope }) => [
  accountName,
  siteNames.length > 0 ? `Sites: ${siteNames.join(', ')}` : 'All Sites',
  groupNames.length > 0 ? `Groups: ${groupNames.join(', ')}` : 'All Groups',
  scope ? `Period: ${getScopePeriodLabel(scope)}` : null,
].filter(Boolean).join(' / ');

const summarizeScopeNamesForFilename = (items = [], allLabel) => {
  const names = (items || []).map((item) => String(item || '').trim()).filter(Boolean);
  if (names.length === 0) return allLabel;
  if (names.length <= 2) return names.join('-');
  return `${names.slice(0, 2).join('-')}-plus-${names.length - 2}`;
};

const buildScopedFilenameBase = ({
  providerName,
  accountName,
  siteNames = [],
  groupNames = [],
  scope,
}) => {
  const periodPart = scope?.startDate && scope?.endDate
    ? `${scope.startDate}_to_${scope.endDate}`
    : (scope?.period || 'period');

  const raw = [
    'report',
    providerName || 'provider',
    accountName || 'account',
    summarizeScopeNamesForFilename(siteNames, 'all-sites'),
    summarizeScopeNamesForFilename(groupNames, 'all-groups'),
    periodPart,
  ].filter(Boolean).join('_');

  return raw
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/-+/g, '-')
    .replace(/^[_-]+|[_-]+$/g, '')
    .slice(0, 180);
};

const normalizeNativeScopeText = (value = '') => String(value || '')
  .replaceAll('&amp;', '&')
  .replaceAll('&lt;', '<')
  .replaceAll('&gt;', '>')
  .replaceAll('&quot;', '"')
  .replaceAll('&#39;', "'")
  .replace(/\*\*(.*?)\*\*/g, '$1')
  .replace(/\s+/g, ' ')
  .trim();

const asObjectRows = (value) => (
  Array.isArray(value)
    ? value.filter((item) => item && typeof item === 'object')
    : []
);

const getScopeCounts = (reportData = {}) => {
  const nativeThreatsInsights = reportData?.native_threats_insights;

  return {
    totalAlerts: nativeThreatsInsights?.overview?.total_threats_since_deployment ?? reportData?.total_alerts ?? reportData?.alerts?.length ?? 0,
    totalEndpoints: nativeThreatsInsights?.overview?.threats_found_on_endpoints ?? reportData?.total_endpoints ?? reportData?.endpoints?.length ?? 0,
  };
};

const getSiteNativeData = (reportData = {}, siteId) => reportData?.native_site_data?.[siteId] || null;

const getOutputFormatLabel = (format) => {
  if (format === 'pdf_docx') return 'PDF + DOCX';
  return String(format || '').toUpperCase();
};

const cloneFormData = (source) => {
  const next = new FormData();
  for (const [key, value] of source.entries()) {
    next.append(key, value);
  }
  return next;
};

const SCOPE_WARMUP_POLL_INTERVAL_MS = 2000;
const SCOPE_WARMUP_POLL_MAX_ATTEMPTS = 30;

const sameIds = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const sameScope = (left, right) => {
  if (!left || !right) return false;
  return (
    left.providerId === right.providerId
    && left.accountId === right.accountId
    && sameIds(left.siteIds || [], right.siteIds || [])
    && sameIds(left.groupIds || [], right.groupIds || [])
    && left.period === right.period
    && (left.startDate || '') === (right.startDate || '')
    && (left.endDate || '') === (right.endDate || '')
  );
};

const sortByName = (items = []) =>
  [...items].sort((left, right) => (left.name || '').localeCompare(right.name || '', undefined, { sensitivity: 'base' }));

const getActiveHierarchy = (hierarchy) => {
  if (!hierarchy) {
    return { accounts: [], sites: [], groups: [] };
  }

  const activeSites = sortByName((hierarchy.sites || []).filter((site) => (site.status || '').toLowerCase() === 'active'));
  const activeSiteIds = new Set(activeSites.map((site) => site.id));
  const activeAccountIds = new Set(activeSites.map((site) => site.account_id).filter(Boolean));

  const activeAccounts = sortByName((hierarchy.accounts || []).filter((account) => activeAccountIds.has(account.id)));
  const activeGroups = sortByName((hierarchy.groups || []).filter((group) => activeSiteIds.has(group.site_id)));

  return {
    accounts: activeAccounts,
    sites: activeSites,
    groups: activeGroups,
  };
};

const getScopedEndpoints = (endpoints, alerts, fetchedScope) => {
  if (!fetchedScope) return [];
  if (!fetchedScope.accountId && !fetchedScope.siteIds?.length && !fetchedScope.groupIds?.length) {
    return endpoints;
  }
  const endpointNames = new Set(alerts.map((alert) => alert.endpoint_name).filter(Boolean));
  return endpoints.filter((endpoint) => endpointNames.has(endpoint.name));
};

const getConfidenceLabel = (confidence) => {
  if (confidence >= 80) return 'Malicious';
  if (confidence >= 50) return 'Suspicious';
  if (confidence > 0) return 'Low Confidence';
  return 'Unknown';
};

const getConfidenceDisplayLabel = (alert) => {
  const rawConfidence = String(alert.metadata?.confidence_level || '').trim().toLowerCase();
  if (rawConfidence === 'malicious') return 'Malicious';
  if (rawConfidence === 'suspicious') return 'Suspicious';
  if (rawConfidence === 'warning') return 'Warning';
  if (rawConfidence === 'benign') return 'Benign';
  return getConfidenceLabel(alert.confidence ?? 0);
};

const humanizeStatusLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replaceAll('_', ' ');
  if (!normalized) return '';
  if (normalized === 'benign' || normalized === 'marked as benign') return 'Marked as Benign';
  if (['resolved', 'mitigated', 'mitigated preemptively', 'mitigated preemptivley', 'killed', 'quarantined', 'remediated'].includes(normalized)) return 'Mitigated';
  if (['not mitigated', 'unresolved', 'active', 'detected'].includes(normalized)) return 'Not Mitigated';
  if (normalized === 'blocked') return 'Blocked';
  if (normalized === 'suspicious') return 'Suspicious';
  if (normalized === 'in progress' || normalized === 'under investigation') return 'Under Investigation';
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusLabel = (alert) => {
  const rawStatusLabel = humanizeStatusLabel(alert.status);
  if (rawStatusLabel === 'Mitigated') return 'Mitigated';

  const analystVerdict = String(
    alert.metadata?.analyst_verdict
      || alert.metadata?.analystVerdict
      || alert.analyst_verdict
      || ''
  ).trim().toLowerCase();

  if (analystVerdict === 'false_positive') return 'Marked as Benign';
  if (analystVerdict === 'true_positive') return 'Mitigated';
  if (analystVerdict === 'suspicious') return 'Not Mitigated';

  return humanizeStatusLabel(alert.metadata?.mitigation_status)
    || humanizeStatusLabel(alert.metadata?.incident_status)
    || humanizeStatusLabel(alert.status)
    || 'Unknown';
};

const getVerdictLabel = (alert) => {
  const analystVerdict = String(
    alert.metadata?.analyst_verdict
      || alert.metadata?.analystVerdict
      || alert.analyst_verdict
      || ''
  ).trim().toLowerCase();

  if (analystVerdict === 'true_positive') return 'True Positive';
  if (analystVerdict === 'false_positive') return 'False Positive';
  if (analystVerdict === 'suspicious') return 'Suspicious';
  if (analystVerdict === 'undefined') return 'Undefined';

  const status = (alert.status || '').toLowerCase();
  if (status === 'resolved' || status === 'blocked') return 'True Positive';
  if (status === 'active' || status === 'unresolved') return 'Needs Review';
  return 'Unknown';
};

const getClassificationLabel = (alert) => {
  const classification = String(alert.metadata?.classification || '').trim();
  if (classification) {
    return classification.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const threatType = String(alert.threat_type || '').trim().toLowerCase();
  if (threatType.includes('ransomware')) return 'Ransomware';
  if (threatType.includes('trojan')) return 'Trojan';
  if (threatType.includes('spyware')) return 'Spyware';
  if (threatType.includes('adware')) return 'Adware';
  if (threatType.includes('worm')) return 'Worm';
  if (threatType.includes('virus')) return 'Virus';
  if (threatType.includes('malware')) return 'Malware';
  if (threatType.endsWith('.exe') || threatType.includes('.dll') || threatType.includes('\\')) return 'Malware';
  if (threatType) return threatType.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  return 'Unknown';
};

const getThreatDetails = (alert) => {
  const candidate = alert.process_name || alert.metadata?.command_line || alert.threat_type || alert.description || 'Unknown';
  return String(candidate).replaceAll('_', ' ');
};

const getIndicatorLabel = (alert) => {
  const candidate = alert.recommendations?.[0]
    || alert.description
    || alert.threat_type
    || alert.process_name
    || 'Unknown';
  return String(candidate).replaceAll('_', ' ');
};

const getSeverityRank = (severity) => {
  switch ((severity || '').toLowerCase()) {
    case 'critical':
      return 4;
    case 'high':
      return 3;
    case 'medium':
      return 2;
    case 'low':
      return 1;
    default:
      return 0;
  }
};

const getBaseScoreFromAlert = (alert) => {
  const riskScore = Number(alert.risk_score || 0);
  if (!riskScore) return '-';
  return (riskScore / 10).toFixed(1);
};

const getPathSegments = (path = '') => String(path || '')
  .trim()
  .replaceAll('/', '\\')
  .split('\\')
  .map((segment) => segment.trim())
  .filter(Boolean);

const getPathBaseName = (path = '') => {
  const segments = getPathSegments(path);
  return segments.length > 0 ? segments[segments.length - 1] : '';
};

const getApplicationName = (alert) => getPathBaseName(alert.process_path) || alert.process_name || alert.metadata?.threat_name || alert.threat_type || 'Unknown';

const getPublisherName = (alert) => {
  const normalizedPath = String(alert.process_path || '').toLowerCase().replaceAll('/', '\\');
  if (
    normalizedPath.includes('\\windows\\')
    || normalizedPath.includes('\\winnt\\')
    || normalizedPath.includes('\\system32\\')
    || normalizedPath.includes('\\syswow64\\')
  ) {
    return 'Microsoft';
  }
  if (normalizedPath.includes('\\mozilla firefox\\')) return 'Mozilla';
  if (normalizedPath.includes('\\google\\chrome\\')) return 'Google';
  if (normalizedPath.includes('\\java\\') || normalizedPath.includes('\\jre') || normalizedPath.includes('\\jdk')) return 'Oracle';

  const segments = getPathSegments(alert.process_path);
  for (let index = 0; index < segments.length; index += 1) {
    const lowerSegment = segments[index].toLowerCase();
    if ((lowerSegment === 'program files' || lowerSegment === 'program files (x86)') && segments[index + 1]) {
      return segments[index + 1];
    }
  }
  return 'Unknown';
};

const getTopMetricLabel = (rows = []) =>
  [...rows].sort((left, right) => (right.count || 0) - (left.count || 0))[0]?.label || '-';

const getPrimaryNativeVerdictLabel = (nativeThreatsInsights) => {
  const verdicts = nativeThreatsInsights?.analyst_verdicts;
  if (!verdicts) return 'See native summary';

  const ranked = [
    { label: 'True Positive', count: verdicts.true_positive || 0 },
    { label: 'False Positive', count: verdicts.false_positive || 0 },
    { label: 'Suspicious', count: verdicts.suspicious || 0 },
    { label: 'Undefined', count: verdicts.undefined || 0 },
  ].sort((left, right) => right.count - left.count);

  return ranked[0]?.count > 0 ? ranked[0].label : 'See native summary';
};

const getPrimaryNativeStatusLabel = (nativeThreatsInsights) => {
  const status = nativeThreatsInsights?.threats_status;
  if (!status) return 'Native Summary';

  const ranked = [
    { label: 'Mitigated', count: status.mitigated || 0 },
    { label: 'Marked as Benign', count: status.marked_as_benign || 0 },
    { label: 'Not Mitigated', count: status.not_mitigated || 0 },
  ].sort((left, right) => right.count - left.count);

  return ranked[0]?.count > 0 ? ranked[0].label : 'Native Summary';
};

const buildNativeIncidentRows = ({ nativeThreatsInsights, scopedEndpoints = [], providerEndpoints = [] }) => {
  const topDevices = asObjectRows(nativeThreatsInsights?.top_devices_at_risk);
  if (topDevices.length === 0) {
    return [];
  }

  const primaryConfidence = getTopMetricLabel(nativeThreatsInsights?.confidence_levels || []);
  const primaryClassification = getTopMetricLabel(nativeThreatsInsights?.classifications || []);
  const primaryVerdict = getPrimaryNativeVerdictLabel(nativeThreatsInsights);
  const primaryStatus = getPrimaryNativeStatusLabel(nativeThreatsInsights);
  const endpointPool = [...scopedEndpoints, ...providerEndpoints];

  return topDevices.map((device, index) => {
    const matchingEndpoint = endpointPool.find((endpoint) => endpoint.name === device.name);
    const threats = device.threats ?? 0;
    const uniqueThreats = device.unique_threats ?? device.uniqueThreats ?? 0;

    return {
      id: `native-${device.name}-${index}`,
      status: primaryStatus,
      threatDetails: `${threats} alert${threats === 1 ? '' : 's'} / ${uniqueThreats} unique threat${uniqueThreats === 1 ? '' : 's'}`,
      confidenceLevel: primaryConfidence,
      endpoint: device.name,
      analystVerdict: primaryVerdict,
      lastReportedIP: matchingEndpoint?.ip || '-',
      classification: primaryClassification,
      path: matchingEndpoint?.policy_mode || device.policy_mode || device.policyMode || '-',
    };
  });
};

const getApplicationPeriodAuxValue = (row = {}) =>
  row.size || row.lastObserved || row.lastSeen || '-';

const getApplicationRowKey = (prefix, app = {}, index = 0) => [
  prefix,
  app.label || app.name || 'unknown',
  app.publisher || 'unknown',
  app.version || 'unknown',
  app.count ?? 'na',
  app.size || app.lastObserved || app.lastSeen || 'na',
  index,
].join('|');

const classifyPlatform = (os = '') => {
  const normalized = (os || '').toLowerCase();
  if (normalized.includes('server')) return 'Server';
  if (normalized.includes('mac')) return 'macOS';
  if (normalized.includes('windows')) return 'Windows';
  if (normalized.includes('linux')) return 'Linux';
  return 'Other';
};

const classifyDeviceType = (endpointName = '', os = '') => {
  const normalizedName = (endpointName || '').toLowerCase();
  const normalizedOS = (os || '').toLowerCase();
  if (normalizedOS.includes('server') || normalizedName.includes('srv') || normalizedName.includes('server')) return 'Server';
  if (normalizedName.includes('desktop') || normalizedName.includes('imac')) return 'Desktop';
  return 'Laptop';
};

const formatActivityType = (activityType = '') => {
  if (!activityType) return 'Unknown Activity';
  if (/^\d+$/.test(activityType)) {
    return `SentinelOne Activity ${activityType}`;
  }
  return activityType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll('_', ' ')
    .trim();
};

const getChangeManagementEventType = (activity = {}) => {
  const text = `${activity.primary_description || ''} ${activity.secondary_description || ''}`.trim().toLowerCase();
  if (text.includes('new install') || text.includes('installed') || text.includes('installation')) return 'New Installations';
  if (text.includes('uninstall') || text.includes('removed')) return 'Uninstalled';
  if (text.includes('decommission') || text.includes('retirement') || text.includes('retired')) return 'Decommissioned';
  if (text.includes('update') || text.includes('upgrade') || text.includes('updated')) return 'Updated';
  if (text.includes('policy')) return 'Policy Changes';
  if (text.includes('threat') || text.includes('mitigated') || text.includes('malicious')) return 'Threat Mitigation';
  return formatActivityType(activity.activity_type);
};

const buildChangeManagementRows = (activities = []) => {
  const fixedRows = [
    { eventType: 'New Installations', notes: 'During report period' },
    { eventType: 'Uninstalled', notes: 'All-time' },
    { eventType: 'Decommissioned', notes: 'Asset retirement' },
  ];

  const counts = {
    'New Installations': 0,
    Uninstalled: 0,
    Decommissioned: 0,
  };

  activities.forEach((activity) => {
    const eventType = getChangeManagementEventType(activity);
    if (Object.prototype.hasOwnProperty.call(counts, eventType)) {
      counts[eventType] += 1;
    }
  });

  return fixedRows.map((row) => ({
    eventType: row.eventType,
    count: counts[row.eventType] || 0,
    notes: row.notes,
  }));
};

const buildSiteSectionData = ({ site, alerts, activities, providerEndpoints, selectedGroups, period, nativeTopDevicesAtRisk, nativeTopIndicators, nativeThreatsInsights, nativeApplicationsInsights, nativeReportScope, xdrRowsBySite }) => {
  const siteAlerts = alerts.filter((alert) => alert.metadata?.site_id === site.id);
  const siteEndpoints = providerEndpoints.filter((endpoint) => endpoint.site_id === site.id);
  const siteGroups = (selectedGroups || []).filter((group) => group.site_id === site.id);
  const siteActivities = (activities || []).filter((activity) => activity.site_id === site.id);
  const scopedOrProviderEndpoints = siteEndpoints;

  const computedIncidentDetails = [...siteAlerts]
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .map((alert) => ({
      id: alert.id,
      status: getStatusLabel(alert),
      threatDetails: getThreatDetails(alert),
      confidenceLevel: getConfidenceDisplayLabel(alert),
      endpoint: alert.endpoint_name || '-',
      analystVerdict: getVerdictLabel(alert),
      lastReportedIP: alert.endpoint_ip || '-',
      classification: getClassificationLabel(alert),
      path: alert.process_path || '-',
    }));
  const incidentDetails = computedIncidentDetails.length > 0
    ? computedIncidentDetails
    : buildNativeIncidentRows({
        nativeThreatsInsights,
        scopedEndpoints: siteEndpoints,
        providerEndpoints,
      });

  const computedTopDevicesAtRisk = Object.values(
    siteAlerts.reduce((acc, alert) => {
      const key = alert.endpoint_name || alert.process_name || alert.id;
      if (!acc[key]) {
        const matchingEndpoint = siteEndpoints.find((endpoint) => endpoint.name === alert.endpoint_name)
          || providerEndpoints.find((endpoint) => endpoint.name === alert.endpoint_name);
        acc[key] = {
          name: key,
          lastReportedIP: alert.endpoint_ip || matchingEndpoint?.ip || '-',
          threats: 0,
          uniqueThreatKeys: new Set(),
          policyMode: alert.metadata?.policy_mode || matchingEndpoint?.policy_mode || '-',
          riskScore: matchingEndpoint?.risk_score || alert.risk_score || 0,
        };
      }
      acc[key].threats += 1;
      acc[key].riskScore = Math.max(acc[key].riskScore, alert.risk_score || 0);
      if (alert.endpoint_ip) {
        acc[key].lastReportedIP = alert.endpoint_ip;
      }
      acc[key].uniqueThreatKeys.add(alert.threat_type || alert.process_name || alert.id);
      return acc;
    }, {})
  )
    .map((device) => ({
      ...device,
      uniqueThreats: device.uniqueThreatKeys.size,
    }))
    .sort((a, b) => (b.riskScore - a.riskScore) || (b.threats - a.threats))
    .slice(0, 5);

  const computedTopIndicators = Object.values(
    siteAlerts.reduce((acc, alert) => {
      const indicator = getIndicatorLabel(alert);
      if (!acc[indicator]) {
        acc[indicator] = {
          label: indicator,
          occurrences: 0,
          agentCounts: {},
          mostSeenAgent: '-',
          lastReportedIP: alert.endpoint_ip || '-',
          lastSeenAt: alert.timestamp,
        };
      }

      acc[indicator].occurrences += 1;
      if (alert.endpoint_name) {
        acc[indicator].agentCounts[alert.endpoint_name] = (acc[indicator].agentCounts[alert.endpoint_name] || 0) + 1;
      }
      if (new Date(alert.timestamp) > new Date(acc[indicator].lastSeenAt)) {
        acc[indicator].lastSeenAt = alert.timestamp;
        acc[indicator].lastReportedIP = alert.endpoint_ip || '-';
      }
      return acc;
    }, {})
  )
    .map((indicator) => ({
      ...indicator,
      mostSeenAgent: Object.entries(indicator.agentCounts)
        .sort((left, right) => right[1] - left[1])[0]?.[0] || '-',
    }))
    .sort((a, b) => b.occurrences - a.occurrences)
    .slice(0, 10);

  const nativeDeviceRows = asObjectRows(nativeTopDevicesAtRisk);
  const topDevicesAtRisk = nativeDeviceRows.length > 0
    ? nativeDeviceRows
    : computedTopDevicesAtRisk;

  const nativeIndicatorRows = asObjectRows(nativeTopIndicators);
  const topIndicators = nativeIndicatorRows.length > 0
    ? nativeIndicatorRows.map((indicator) => ({
        label: String(indicator.indicator || indicator.label || 'Unknown'),
        occurrences: indicator.occurrences,
        mostSeenAgent: indicator.most_seen_agent || indicator.mostSeenAgent || '-',
        lastReportedIP: (providerEndpoints.find((endpoint) => endpoint.name === (indicator.most_seen_agent || indicator.mostSeenAgent)) || {}).ip || '-',
      }))
    : computedTopIndicators;

  const enterpriseRiskProfileDetails = Object.values(
    siteAlerts.reduce((acc, alert) => {
      const key = `${alert.endpoint_name || 'Unknown'}|${alert.process_path || alert.process_name || alert.id}`;
      if (!acc[key]) {
        acc[key] = {
          endpoint: alert.endpoint_name || '-',
          lastReportedIP: alert.endpoint_ip || '-',
          pathOrFileName: alert.process_path || alert.process_name || '-',
          lastSeenAt: alert.timestamp,
        };
      }

      if (new Date(alert.timestamp) > new Date(acc[key].lastSeenAt)) {
        acc[key].lastSeenAt = alert.timestamp;
        acc[key].lastReportedIP = alert.endpoint_ip || acc[key].lastReportedIP;
        acc[key].pathOrFileName = alert.process_path || alert.process_name || acc[key].pathOrFileName;
      }

      return acc;
    }, {})
  )
    .sort((a, b) => new Date(b.lastSeenAt) - new Date(a.lastSeenAt))
    .slice(0, 20);

  const windowsApplicationStats = Object.values(
    siteAlerts.reduce((acc, alert) => {
      const isWindows = (alert.endpoint_os || '').toLowerCase().includes('windows');
      const appName = getApplicationName(alert);
      if (!isWindows || !appName) return acc;
      if (!acc[appName]) {
        acc[appName] = {
          label: appName,
          count: 0,
          lastSeen: alert.timestamp,
          publisher: getPublisherName(alert),
          version: (siteEndpoints.find((endpoint) => endpoint.name === alert.endpoint_name)
            || providerEndpoints.find((endpoint) => endpoint.name === alert.endpoint_name))?.agent_version || '-',
          size: '-',
        };
      }
      acc[appName].count += 1;
      if (new Date(alert.timestamp) > new Date(acc[appName].lastSeen)) {
        acc[appName].lastSeen = alert.timestamp;
      }
      return acc;
    }, {})
  );

  const topWindowsApplications = [...windowsApplicationStats]
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const topNewUpdatedWindowsApplications = [...windowsApplicationStats]
    .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen))
    .slice(0, 8);

  const nativeWindowsApplicationsAllTime = asObjectRows(nativeApplicationsInsights?.top_windows_applications_all_time);
  const nativeWindowsApplicationsPeriod = asObjectRows(nativeApplicationsInsights?.top_windows_applications_period);
  const topMacOSApplications = asObjectRows(nativeApplicationsInsights?.top_macos_applications_all_time);
  const topNewUpdatedMacOSApplications = asObjectRows(nativeApplicationsInsights?.top_macos_applications_period);
  const topLinuxApplications = asObjectRows(nativeApplicationsInsights?.top_linux_applications_all_time);
  const topNewUpdatedLinuxApplications = asObjectRows(nativeApplicationsInsights?.top_linux_applications_period);

  const applicationVulnerabilityDetails = Object.values(
    siteAlerts.reduce((acc, alert) => {
      if (!(alert.endpoint_os || '').toLowerCase().includes('windows')) {
        return acc;
      }

      const applicationName = getApplicationName(alert);
      if (!acc[applicationName]) {
        acc[applicationName] = {
          id: applicationName,
          applicationName,
          vendor: getPublisherName(alert),
          highestSeverity: alert.severity || 'unknown',
          highestSeverityRank: getSeverityRank(alert.severity),
          highestNvdBaseScore: getBaseScoreFromAlert(alert),
          cves: new Set(),
          endpoints: new Set(),
          detectionDate: alert.timestamp,
          lastSeenAt: alert.timestamp,
        };
      }

      const detail = acc[applicationName];
      const severityRank = getSeverityRank(alert.severity);
      if (severityRank > detail.highestSeverityRank) {
        detail.highestSeverity = alert.severity || 'unknown';
        detail.highestSeverityRank = severityRank;
        detail.highestNvdBaseScore = getBaseScoreFromAlert(alert);
      } else if (severityRank === detail.highestSeverityRank) {
        const nextScore = Number(getBaseScoreFromAlert(alert));
        const currentScore = Number(detail.highestNvdBaseScore);
        if (!Number.isNaN(nextScore) && (Number.isNaN(currentScore) || nextScore > currentScore)) {
          detail.highestNvdBaseScore = nextScore.toFixed(1);
        }
      }

      const cveMatch = `${alert.description || ''} ${alert.recommendations?.join(' ') || ''}`.match(/CVE-\d{4}-\d+/gi) || [];
      cveMatch.forEach((cve) => detail.cves.add(cve.toUpperCase()));
      if (alert.endpoint_name) {
        detail.endpoints.add(alert.endpoint_name);
      }
      if (new Date(alert.timestamp) < new Date(detail.detectionDate)) {
        detail.detectionDate = alert.timestamp;
      }
      if (new Date(alert.timestamp) > new Date(detail.lastSeenAt)) {
        detail.lastSeenAt = alert.timestamp;
      }

      return acc;
    }, {})
  )
    .map((detail) => ({
      id: detail.id,
      applicationName: detail.applicationName,
      vendor: detail.vendor,
      highestSeverity: detail.highestSeverity,
      highestNvdBaseScore: detail.highestNvdBaseScore,
      numberOfCVEs: String(detail.cves.size ?? 0),
      numberOfEndpoints: detail.endpoints.size,
      applicationDetectionDate: detail.detectionDate,
      daysFromDetection: Math.max(0, Math.floor((Date.now() - new Date(detail.detectionDate).getTime()) / (1000 * 60 * 60 * 24))),
      lastSeenAt: detail.lastSeenAt,
    }))
    .sort((a, b) => new Date(b.lastSeenAt) - new Date(a.lastSeenAt))
    .slice(0, 20);

  const sentinelOneAssetView = Object.values(
    scopedOrProviderEndpoints.reduce((acc, endpoint) => {
      const platform = classifyPlatform(endpoint.os);
      if (!acc[platform]) {
        acc[platform] = {
          platform,
          total: 0,
          laptops: 0,
          desktops: 0,
          servers: 0,
        };
      }
      acc[platform].total += 1;
      const deviceType = classifyDeviceType(endpoint.name, endpoint.os);
      if (deviceType === 'Server') {
        acc[platform].servers += 1;
      } else if (deviceType === 'Desktop') {
        acc[platform].desktops += 1;
      } else {
        acc[platform].laptops += 1;
      }
      return acc;
    }, {})
  ).sort((a, b) => a.platform.localeCompare(b.platform));

  const sentinelOneChangeManagement = buildChangeManagementRows(siteActivities);
  const xdrTopProtocolServiceUsages = asObjectRows(xdrRowsBySite?.[site.id]);

  return {
    site,
    reportScope: normalizeNativeScopeText(nativeReportScope || ''),
    siteGroups,
    siteAlerts,
    siteActivities,
    siteEndpoints,
    incidentDetails,
    topDevicesAtRisk,
    topIndicators,
    enterpriseRiskProfileDetails,
    applicationVulnerabilityDetails,
    topWindowsApplications: nativeWindowsApplicationsAllTime.length > 0 ? nativeWindowsApplicationsAllTime : topWindowsApplications,
    topNewUpdatedWindowsApplications: nativeWindowsApplicationsPeriod.length > 0 ? nativeWindowsApplicationsPeriod : topNewUpdatedWindowsApplications,
    topMacOSApplications,
    topNewUpdatedMacOSApplications,
    topLinuxApplications,
    topNewUpdatedLinuxApplications,
    sentinelOneAssetView,
    sentinelOneChangeManagement,
    xdrTopProtocolServiceUsages,
    period,
  };
};

const ReportGenerator = () => {
  const [edrProviders, setEdrProviders] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [allEndpoints, setAllEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingScopeData, setFetchingScopeData] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [fetchedScope, setFetchedScope] = useState(null);
  const [incidentPage, setIncidentPage] = useState(1);
  const [incidentPages, setIncidentPages] = useState({});
  const [aiSettings, setAISettings] = useState({ gemini_configured: false, gemini_key_hint: '' });
  const [warmingScopeData, setWarmingScopeData] = useState(false);
  const warmupPollTokenRef = useRef(0);
  
  const [formData, setFormData] = useState({
    edr_provider: '',
    account_id: '',
    site_id: '',
    group_id: '',
    format: 'pdf',
    language: 'en',
    theme_color: '#2f5496',
    logo_file: null, // Store File object
    engine: 'system', // system, gemini
    period: 'daily', // hourly, daily, weekly, monthly, yearly, all_time
    start_date: '',
    end_date: '',
  });

  const stopScopeWarmupPolling = () => {
    warmupPollTokenRef.current += 1;
    setWarmingScopeData(false);
  };

  const buildScopeQueryParams = (scope) => ({
    provider_id: scope.providerId,
    account_id: scope.accountId,
    site_ids: (scope.siteIds || []).join(','),
    group_ids: (scope.groupIds || []).join(','),
    period: scope.period,
    start_date: scope.startDate,
    end_date: scope.endDate,
  });

  const startScopeWarmupPolling = (scope) => {
    if (!scope?.providerId || !scope?.accountId) {
      return;
    }

    const pollToken = warmupPollTokenRef.current + 1;
    warmupPollTokenRef.current = pollToken;
    setWarmingScopeData(true);

    const pollScopeData = async (attempt) => {
      if (warmupPollTokenRef.current !== pollToken) {
        return;
      }

      try {
        const response = await apiService.getEDRReportData(buildScopeQueryParams(scope));
        if (warmupPollTokenRef.current !== pollToken) {
          return;
        }

        setReportData(response);
        setFetchedScope(scope);

        const stillWarming = Boolean(response?.native_regeneration_skipped);
        if (!stillWarming) {
          setWarmingScopeData(false);
          return;
        }
      } catch (pollError) {
        console.warn('Scoped native warmup polling failed:', pollError);
      }

      if (warmupPollTokenRef.current !== pollToken) {
        return;
      }

      if (attempt >= SCOPE_WARMUP_POLL_MAX_ATTEMPTS) {
        setWarmingScopeData(false);
        return;
      }

      setTimeout(() => {
        pollScopeData(attempt + 1);
      }, SCOPE_WARMUP_POLL_INTERVAL_MS);
    };

    pollScopeData(1);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => () => {
    warmupPollTokenRef.current += 1;
  }, []);

  const filteredHierarchy = useMemo(
    () => filterHierarchyByProvider(hierarchyData, formData.edr_provider),
    [hierarchyData, formData.edr_provider]
  );
  const activeHierarchy = useMemo(
    () => getActiveHierarchy(filteredHierarchy),
    [filteredHierarchy]
  );
  const hierarchySlices = useMemo(
    () => getHierarchySlices(activeHierarchy, formData.account_id, selectedSiteIds),
    [activeHierarchy, formData.account_id, selectedSiteIds]
  );
  const selectedProvider = edrProviders.find((provider) => provider.id === formData.edr_provider);
  const selectedAccount = hierarchySlices.accounts.find((account) => account.id === formData.account_id);
  const selectedScope = useMemo(() => ({
    providerId: formData.edr_provider,
    accountId: formData.account_id,
    siteIds: selectedSiteIds,
    groupIds: selectedGroupIds,
    period: formData.period,
    startDate: formData.start_date,
    endDate: formData.end_date,
  }), [formData.account_id, formData.edr_provider, formData.end_date, formData.period, formData.start_date, selectedGroupIds, selectedSiteIds]);
  const hasFetchedCurrentScope = Boolean(fetchedScope && sameScope(fetchedScope, selectedScope));
  const effectiveFetchedScope = hasFetchedCurrentScope ? fetchedScope : null;
  const fetchedSites = hierarchySlices.sites.filter((site) => (effectiveFetchedScope?.siteIds || []).includes(site.id));
  const fetchedGroups = hierarchySlices.groups.filter((group) => (effectiveFetchedScope?.groupIds || []).includes(group.id));
  const selectedSiteNames = hierarchySlices.sites
    .filter((site) => selectedSiteIds.includes(site.id))
    .map((site) => site.name);
  const selectedGroupNames = hierarchySlices.groups
    .filter((group) => selectedGroupIds.includes(group.id))
    .map((group) => group.name);
  const scopedAlerts = reportData?.alerts || [];
  const scopedActivities = reportData?.activities || [];
  const scopedEndpoints = reportData?.endpoints || [];
  const nativeThreatsInsights = reportData?.native_threats_insights || null;
  const nativeApplicationsInsights = reportData?.native_applications_insights || null;
  const nativeTopDevicesAtRisk = asObjectRows(reportData?.top_devices_at_risk);
  const nativeTopIndicators = asObjectRows(reportData?.top_indicators);
  const xdrRowsBySite = reportData?.xdr_top_protocol_service_usages_by_site || {};
  const { totalAlerts: summaryAlertCount, totalEndpoints: summaryEndpointCount } = getScopeCounts(reportData);
  const providerEndpoints = useMemo(
    () => allEndpoints.filter((endpoint) => endpoint.provider_id === formData.edr_provider),
    [allEndpoints, formData.edr_provider]
  );
  const siteSections = useMemo(() => {
    if (!effectiveFetchedScope) {
      return [];
    }

    const selectedGroups = hierarchySlices.groups.filter((group) =>
      selectedGroupIds.length === 0 || selectedGroupIds.includes(group.id)
    );
    const selectedSites = hierarchySlices.sites.filter((site) =>
      selectedSiteIds.length === 0 || selectedSiteIds.includes(site.id)
    );

    return selectedSites.map((site) => {
      const siteNativeData = getSiteNativeData(reportData, site.id);
      const siteNativeThreatsInsights = siteNativeData?.threats_insights || null;
      const siteNativeApplicationsInsights = siteNativeData?.applications_insights || null;
      const siteNativeReportScope = normalizeNativeScopeText(siteNativeData?.report_scope || '');

      return buildSiteSectionData({
        site,
        alerts: scopedAlerts,
        activities: scopedActivities,
        providerEndpoints,
        selectedGroups,
        period: effectiveFetchedScope.period,
        nativeTopDevicesAtRisk: siteNativeThreatsInsights?.top_devices_at_risk || [],
        nativeTopIndicators: siteNativeThreatsInsights?.top_indicators || [],
        nativeThreatsInsights: siteNativeThreatsInsights,
        nativeApplicationsInsights: siteNativeApplicationsInsights,
        nativeReportScope: siteNativeReportScope,
        xdrRowsBySite,
      });
    });
  }, [effectiveFetchedScope, hierarchySlices.groups, hierarchySlices.sites, providerEndpoints, scopedAlerts, scopedActivities, selectedGroupIds, selectedSiteIds, nativeThreatsInsights, nativeApplicationsInsights, reportData, xdrRowsBySite]);

  useEffect(() => {
    stopScopeWarmupPolling();
    setFormData((prev) => ({
      ...prev,
      account_id: '',
    }));
    setSelectedSiteIds([]);
    setSelectedGroupIds([]);
    setFetchedScope(null);
    setIncidentPages({});
  }, [formData.edr_provider]);

  useEffect(() => {
    if (formData.account_id && !hierarchySlices.accounts.some((account) => account.id === formData.account_id)) {
      stopScopeWarmupPolling();
      setFormData((prev) => ({
        ...prev,
        account_id: '',
      }));
      setSelectedSiteIds([]);
      setSelectedGroupIds([]);
      return;
    }
    setSelectedSiteIds((prev) => {
      const next = prev.filter((siteId) => hierarchySlices.sites.some((site) => site.id === siteId));
      return sameIds(prev, next) ? prev : next;
    });
    setSelectedGroupIds((prev) => {
      const next = prev.filter((groupId) => hierarchySlices.groups.some((group) => group.id === groupId));
      return sameIds(prev, next) ? prev : next;
    });
  }, [formData.account_id, hierarchySlices.accounts, hierarchySlices.sites, hierarchySlices.groups]);

  useEffect(() => {
    if (!aiSettings.gemini_configured && formData.engine === 'gemini') {
      setFormData((prev) => ({ ...prev, engine: 'system' }));
    }
  }, [aiSettings.gemini_configured, formData.engine]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersData, languagesData, hierarchyResponse, endpointsResponse] = await Promise.all([
        apiService.getEDRProviders(),
        apiService.getLanguages(),
        apiService.getEDRHierarchy(),
        apiService.getEDREndpoints(),
      ]);
      setEdrProviders(providersData);
      setLanguages(languagesData);
      setHierarchyData(hierarchyResponse.data || hierarchyResponse);
      setAllEndpoints(endpointsResponse || []);
      
      if (providersData.length > 0) {
        setFormData(prev => ({ ...prev, edr_provider: providersData[0].id }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load required data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    stopScopeWarmupPolling();
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (name === 'account_id') {
      setFormData((prev) => ({ ...prev, account_id: value }));
      setSelectedSiteIds([]);
      setSelectedGroupIds([]);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleOpenGenerateModal = async () => {
    try {
      const aiSettingsResponse = await apiService.getAISettings();
      setAISettings(aiSettingsResponse || { gemini_configured: false, gemini_key_hint: '' });
    } catch (error) {
      console.warn('Unable to load AI settings for generate modal:', error);
      setAISettings({ gemini_configured: false, gemini_key_hint: '' });
    } finally {
      setShowGenerateModal(true);
    }
  };

  const handleFetchScopeData = async () => {
    const nextScope = selectedScope;

    try {
      stopScopeWarmupPolling();
      setError('');
      setIncidentPage(1);
      setIncidentPages({});
      setFetchingScopeData(true);
      const response = await apiService.getEDRReportData(buildScopeQueryParams(nextScope));
      setReportData(response);
      setFetchedScope(nextScope);
      if (response?.native_regeneration_skipped) {
        startScopeWarmupPolling(nextScope);
      }
      return { response, scope: nextScope };
    } catch (fetchError) {
      console.error('Error fetching scoped report data:', fetchError);
      setError('Failed to fetch scoped report data.');
      throw fetchError;
    } finally {
      setFetchingScopeData(false);
    }
  };

  const toggleSiteSelection = (siteId) => {
    stopScopeWarmupPolling();
    setSelectedSiteIds((prev) => {
      if (!siteId) {
        return [];
      }
      return prev.includes(siteId)
        ? prev.filter((id) => id !== siteId)
        : [...prev, siteId];
    });
    setSelectedGroupIds([]);
  };

  const toggleGroupSelection = (groupId) => {
    stopScopeWarmupPolling();
    setSelectedGroupIds((prev) => {
      if (!groupId) {
        return [];
      }
      return prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId];
    });
  };

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      setError('');

      let activeScope = fetchedScope;
      let activeReportData = reportData;

      if (!fetchedScope || !reportData || !sameScope(fetchedScope, selectedScope)) {
        const fetchResult = await handleFetchScopeData();
        activeScope = fetchResult.scope;
        activeReportData = fetchResult.response;
      }

      if (!activeScope || !activeReportData) {
        setError('Unable to fetch scope data before generating the report.');
        return;
      }

      const activeScopeSummary = buildScopeSummary({
        accountName: selectedAccount?.name,
        siteNames: selectedSiteNames,
        groupNames: selectedGroupNames,
        scope: activeScope,
      });
      const activeScopeSiteNames = selectedSiteNames.length > 0
        ? selectedSiteNames
        : (activeScope.siteIds || []);
      const activeScopeGroupNames = selectedGroupNames.length > 0
        ? selectedGroupNames
        : (activeScope.groupIds || []);
      const scopeFilenameBase = buildScopedFilenameBase({
        providerName: selectedProvider?.name || activeScope.providerId,
        accountName: selectedAccount?.name || activeScope.accountId,
        siteNames: activeScopeSiteNames,
        groupNames: activeScopeGroupNames,
        scope: activeScope,
      });
      const { totalAlerts: activeTotalAlerts, totalEndpoints: activeTotalEndpoints } = getScopeCounts(activeReportData);
      
      // Use FormData for multipart/form-data support (for logo file)
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          submitData.append(key, formData[key]);
        }
      });
      submitData.set('structure', activeScope ? 'industrial' : 'standard');
      submitData.set('allow_native_regeneration', 'false');
      submitData.set('edr_provider', activeScope.providerId || formData.edr_provider);
      submitData.set('account_id', activeScope.accountId || '');
      submitData.set('period', activeScope.period || formData.period);
      submitData.set('start_date', activeScope.startDate || formData.start_date || '');
      submitData.set('end_date', activeScope.endDate || formData.end_date || '');
      if (activeScope.siteIds.length > 0) {
        submitData.set('site_id', activeScope.siteIds[0]);
        activeScope.siteIds.forEach((siteId) => submitData.append('site_ids', siteId));
      }
      if (activeScope.groupIds.length > 0) {
        submitData.set('group_id', activeScope.groupIds[0]);
        activeScope.groupIds.forEach((groupId) => submitData.append('group_ids', groupId));
      }

      const formatLabel = getOutputFormatLabel(formData.format);

      if (formData.format === 'json') {
        const result = await apiService.generateReport(submitData);
        setReport({
          ...result.data,
          summary: {
            scopeSummary: activeScopeSummary,
            totalAlerts: activeTotalAlerts,
            totalEndpoints: activeTotalEndpoints,
            period: getScopePeriodLabel(activeScope),
            format: formatLabel,
          },
          message: result.notice
            ? `Report generated with ${result.engine.toUpperCase()}. ${result.notice}`
            : `Report generated with ${result.engine.toUpperCase()}.`,
        });
      } else {
        const formatSubmitData = cloneFormData(submitData);
        formatSubmitData.set('format', formData.format);
        const downloadExtension = formData.format === 'pdf_docx' ? 'zip' : formData.format;
        const result = await apiService.downloadReport(formatSubmitData, downloadExtension, scopeFilenameBase);
        const engineSummary = result.engine.toUpperCase();
        const noticeSummary = result.notice || '';
        setReport({
          summary: {
            scopeSummary: activeScopeSummary,
            totalAlerts: activeTotalAlerts,
            totalEndpoints: activeTotalEndpoints,
            period: getScopePeriodLabel(activeScope),
            format: formatLabel,
          },
          message: noticeSummary
            ? `Report downloaded as ${formatLabel} using ${engineSummary}. ${noticeSummary}`
            : `Report downloaded as ${formatLabel} using ${engineSummary}.`,
        });
      }
      setShowGenerateModal(false);
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const computedIncidentDetails = [...scopedAlerts]
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .map((alert) => ({
      id: alert.id,
      status: getStatusLabel(alert),
      threatDetails: getThreatDetails(alert),
      confidenceLevel: getConfidenceDisplayLabel(alert),
      endpoint: alert.endpoint_name || '-',
      analystVerdict: getVerdictLabel(alert),
      lastReportedIP: alert.endpoint_ip || '-',
      classification: getClassificationLabel(alert),
      path: alert.process_path || '-',
    }));
  const incidentDetails = computedIncidentDetails.length > 0
    ? computedIncidentDetails
    : buildNativeIncidentRows({
        nativeThreatsInsights,
        scopedEndpoints,
        providerEndpoints,
      });
  const incidentsPerPage = 8;
  const totalIncidentPages = Math.max(1, Math.ceil(incidentDetails.length / incidentsPerPage));
  const pagedIncidentDetails = incidentDetails.slice(
    (incidentPage - 1) * incidentsPerPage,
    incidentPage * incidentsPerPage
  );
  const visibleIncidentPages = Array.from({ length: totalIncidentPages }, (_, index) => index + 1)
    .filter((pageNumber) => {
      if (totalIncidentPages <= 7) return true;
      if (pageNumber === 1 || pageNumber === totalIncidentPages) return true;
      return Math.abs(pageNumber - incidentPage) <= 1;
    });
  const scopeSummary = buildScopeSummary({
    accountName: selectedAccount?.name,
    siteNames: fetchedSites.map((site) => site.name),
    groupNames: fetchedGroups.map((group) => group.name),
    scope: fetchedScope,
  });
  const computedTopDevicesAtRisk = Object.values(
    scopedAlerts.reduce((acc, alert) => {
      const key = alert.endpoint_name || alert.process_name || alert.id;
      if (!acc[key]) {
        const matchingEndpoint = scopedEndpoints.find((endpoint) => endpoint.name === alert.endpoint_name);
        acc[key] = {
          name: key,
          lastReportedIP: alert.endpoint_ip || matchingEndpoint?.ip || '-',
          threats: 0,
          uniqueThreatKeys: new Set(),
          policyMode: alert.metadata?.policy_mode || matchingEndpoint?.policy_mode || '-',
          riskScore: matchingEndpoint?.risk_score || alert.risk_score || 0,
        };
      }
      acc[key].threats += 1;
      acc[key].riskScore = Math.max(acc[key].riskScore, alert.risk_score || 0);
      if (alert.endpoint_ip) {
        acc[key].lastReportedIP = alert.endpoint_ip;
      }
      acc[key].uniqueThreatKeys.add(alert.threat_type || alert.process_name || alert.id);
      return acc;
    }, {})
  )
    .map((device) => ({
      ...device,
      uniqueThreats: device.uniqueThreatKeys.size,
    }))
    .sort((a, b) => (b.riskScore - a.riskScore) || (b.threats - a.threats))
    .slice(0, 5);
  const computedTopIndicators = Object.values(
    scopedAlerts.reduce((acc, alert) => {
      const indicator = getIndicatorLabel(alert);
      if (!acc[indicator]) {
        acc[indicator] = {
          label: indicator,
          occurrences: 0,
          agentCounts: {},
          mostSeenAgent: '-',
          lastReportedIP: alert.endpoint_ip || '-',
          lastSeenAt: alert.timestamp,
        };
      }

      acc[indicator].occurrences += 1;
      if (alert.endpoint_name) {
        acc[indicator].agentCounts[alert.endpoint_name] = (acc[indicator].agentCounts[alert.endpoint_name] || 0) + 1;
      }
      if (new Date(alert.timestamp) > new Date(acc[indicator].lastSeenAt)) {
        acc[indicator].lastSeenAt = alert.timestamp;
        acc[indicator].lastReportedIP = alert.endpoint_ip || '-';
      }
      return acc;
    }, {})
  )
    .map((indicator) => ({
      ...indicator,
      mostSeenAgent: Object.entries(indicator.agentCounts)
        .sort((left, right) => right[1] - left[1])[0]?.[0] || '-',
    }))
    .sort((a, b) => b.occurrences - a.occurrences)
    .slice(0, 10);
  const topDevicesAtRisk = nativeTopDevicesAtRisk.length > 0
    ? nativeTopDevicesAtRisk.map((device) => ({
        name: device.name,
        lastReportedIP: (scopedEndpoints.find((endpoint) => endpoint.name === device.name) || providerEndpoints.find((endpoint) => endpoint.name === device.name) || {}).ip || '-',
        threats: device.threats,
        uniqueThreats: device.unique_threats ?? device.uniqueThreats,
        policyMode: device.policy_mode ?? device.policyMode ?? '-',
      }))
    : computedTopDevicesAtRisk;
  const topIndicators = nativeTopIndicators.length > 0
    ? nativeTopIndicators.map((indicator) => ({
        label: String(indicator.indicator || indicator.label || 'Unknown'),
        occurrences: indicator.occurrences,
        mostSeenAgent: indicator.most_seen_agent || indicator.mostSeenAgent || '-',
        lastReportedIP: (scopedEndpoints.find((endpoint) => endpoint.name === (indicator.most_seen_agent || indicator.mostSeenAgent)) || providerEndpoints.find((endpoint) => endpoint.name === (indicator.most_seen_agent || indicator.mostSeenAgent)) || {}).ip || '-',
      }))
    : computedTopIndicators;
  const enterpriseRiskProfileDetails = Object.values(
    scopedAlerts.reduce((acc, alert) => {
      const key = `${alert.endpoint_name || 'Unknown'}|${alert.process_path || alert.process_name || alert.id}`;
      if (!acc[key]) {
        acc[key] = {
          endpoint: alert.endpoint_name || '-',
          lastReportedIP: alert.endpoint_ip || '-',
          pathOrFileName: alert.process_path || alert.process_name || '-',
          lastSeenAt: alert.timestamp,
        };
      }

      if (new Date(alert.timestamp) > new Date(acc[key].lastSeenAt)) {
        acc[key].lastSeenAt = alert.timestamp;
        acc[key].lastReportedIP = alert.endpoint_ip || acc[key].lastReportedIP;
        acc[key].pathOrFileName = alert.process_path || alert.process_name || acc[key].pathOrFileName;
      }

      return acc;
    }, {})
  )
    .sort((a, b) => new Date(b.lastSeenAt) - new Date(a.lastSeenAt))
    .slice(0, 20);
  const windowsApplicationStats = Object.values(
    scopedAlerts.reduce((acc, alert) => {
      const isWindows = (alert.endpoint_os || '').toLowerCase().includes('windows');
      const appName = getApplicationName(alert);
      if (!isWindows || !appName) return acc;
      if (!acc[appName]) {
        acc[appName] = {
          label: appName,
          count: 0,
          lastSeen: alert.timestamp,
          publisher: getPublisherName(alert),
          version: scopedEndpoints.find((endpoint) => endpoint.name === alert.endpoint_name)?.agent_version || '-',
          size: '-',
        };
      }
      acc[appName].count += 1;
      if (new Date(alert.timestamp) > new Date(acc[appName].lastSeen)) {
        acc[appName].lastSeen = alert.timestamp;
      }
      return acc;
    }, {})
  );
  const topWindowsApplications = [...windowsApplicationStats]
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
  const topNewUpdatedWindowsApplications = [...windowsApplicationStats]
    .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen))
    .slice(0, 8);
  const nativeWindowsApplicationsAllTime = asObjectRows(nativeApplicationsInsights?.top_windows_applications_all_time);
  const nativeWindowsApplicationsPeriod = asObjectRows(nativeApplicationsInsights?.top_windows_applications_period);
  const resolvedTopWindowsApplications = nativeWindowsApplicationsAllTime.length > 0
    ? nativeWindowsApplicationsAllTime
    : topWindowsApplications;
  const resolvedTopNewUpdatedWindowsApplications = nativeWindowsApplicationsPeriod.length > 0
    ? nativeWindowsApplicationsPeriod
    : topNewUpdatedWindowsApplications;
  const topMacOSApplications = asObjectRows(nativeApplicationsInsights?.top_macos_applications_all_time);
  const topNewUpdatedMacOSApplications = asObjectRows(nativeApplicationsInsights?.top_macos_applications_period);
  const topLinuxApplications = asObjectRows(nativeApplicationsInsights?.top_linux_applications_all_time);
  const topNewUpdatedLinuxApplications = asObjectRows(nativeApplicationsInsights?.top_linux_applications_period);
  const applicationVulnerabilityDetails = Object.values(
    scopedAlerts.reduce((acc, alert) => {
      if (!(alert.endpoint_os || '').toLowerCase().includes('windows')) {
        return acc;
      }

      const applicationName = getApplicationName(alert);
      if (!acc[applicationName]) {
        acc[applicationName] = {
          id: applicationName,
          applicationName,
          vendor: getPublisherName(alert),
          highestSeverity: alert.severity || 'unknown',
          highestSeverityRank: getSeverityRank(alert.severity),
          highestNvdBaseScore: getBaseScoreFromAlert(alert),
          cves: new Set(),
          endpoints: new Set(),
          detectionDate: alert.timestamp,
          lastSeenAt: alert.timestamp,
        };
      }

      const detail = acc[applicationName];
      const severityRank = getSeverityRank(alert.severity);
      if (severityRank > detail.highestSeverityRank) {
        detail.highestSeverity = alert.severity || 'unknown';
        detail.highestSeverityRank = severityRank;
        detail.highestNvdBaseScore = getBaseScoreFromAlert(alert);
      } else if (severityRank === detail.highestSeverityRank) {
        const nextScore = Number(getBaseScoreFromAlert(alert));
        const currentScore = Number(detail.highestNvdBaseScore);
        if (!Number.isNaN(nextScore) && (Number.isNaN(currentScore) || nextScore > currentScore)) {
          detail.highestNvdBaseScore = nextScore.toFixed(1);
        }
      }

      const cveMatch = `${alert.description || ''} ${alert.recommendations?.join(' ') || ''}`.match(/CVE-\d{4}-\d+/gi) || [];
      cveMatch.forEach((cve) => detail.cves.add(cve.toUpperCase()));
      if (alert.endpoint_name) {
        detail.endpoints.add(alert.endpoint_name);
      }
      if (new Date(alert.timestamp) < new Date(detail.detectionDate)) {
        detail.detectionDate = alert.timestamp;
      }
      if (new Date(alert.timestamp) > new Date(detail.lastSeenAt)) {
        detail.lastSeenAt = alert.timestamp;
      }

      return acc;
    }, {})
  )
    .map((detail) => ({
      id: detail.id,
      applicationName: detail.applicationName,
      vendor: detail.vendor,
      highestSeverity: detail.highestSeverity,
      highestNvdBaseScore: detail.highestNvdBaseScore,
      numberOfCVEs: String(detail.cves.size ?? 0),
      numberOfEndpoints: detail.endpoints.size,
      applicationDetectionDate: detail.detectionDate,
      daysFromDetection: Math.max(0, Math.floor((Date.now() - new Date(detail.detectionDate).getTime()) / (1000 * 60 * 60 * 24))),
      lastSeenAt: detail.lastSeenAt,
    }))
    .sort((a, b) => new Date(b.lastSeenAt) - new Date(a.lastSeenAt))
    .slice(0, 20);
  const scopedOrProviderEndpoints = scopedEndpoints.length > 0 ? scopedEndpoints : providerEndpoints;
  const classifyPlatform = (os = '') => {
    const normalized = (os || '').toLowerCase();
    if (normalized.includes('server')) return 'Server';
    if (normalized.includes('mac')) return 'macOS';
    if (normalized.includes('windows')) return 'Windows';
    if (normalized.includes('linux')) return 'Linux';
    return 'Other';
  };
  const classifyDeviceType = (endpointName = '', os = '') => {
    const normalizedName = (endpointName || '').toLowerCase();
    const normalizedOS = (os || '').toLowerCase();
    if (normalizedOS.includes('server') || normalizedName.includes('srv') || normalizedName.includes('server')) return 'Server';
    if (normalizedName.includes('desktop') || normalizedName.includes('imac')) return 'Desktop';
    return 'Laptop';
  };
  const sentinelOneAssetView = Object.values(
    scopedOrProviderEndpoints.reduce((acc, endpoint) => {
      const platform = classifyPlatform(endpoint.os);
      if (!acc[platform]) {
        acc[platform] = {
          platform,
          total: 0,
          laptops: 0,
          desktops: 0,
          servers: 0,
        };
      }
      acc[platform].total += 1;
      const deviceType = classifyDeviceType(endpoint.name, endpoint.os);
      if (deviceType === 'Server') {
        acc[platform].servers += 1;
      } else if (deviceType === 'Desktop') {
        acc[platform].desktops += 1;
      } else {
        acc[platform].laptops += 1;
      }
      return acc;
    }, {})
  ).sort((a, b) => a.platform.localeCompare(b.platform));
  const sentinelOneChangeManagement = buildChangeManagementRows(scopedActivities);
  const xdrTopProtocolServiceUsages = asObjectRows(reportData?.xdr_top_protocol_service_usages);
  if (loading) {
    return <LoadingSpinner message="Loading report generator..." />;
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">
                {/* <FaFileAlt className="me-2" /> */}
                Generate Report
              </h2>
              <p className="text-muted mb-0">Create comprehensive security reports with customizable scope and formatting</p>
            </div>
            <Button variant="primary" onClick={handleOpenGenerateModal} className="d-flex align-items-center">
              <FaRocket className="me-2" />
              Generate Report
            </Button>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')} className="d-flex align-items-center">
              <span className="material-symbols-outlined me-2">warning</span>
              <span className="flex-grow-1">{error}</span>
            </Alert>
          )}

          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex align-items-center">
                <span className="material-symbols-outlined me-2 text-primary">settings</span>
                <h5 className="mb-0">Report Configuration</h5>
              </div>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="d-flex align-items-center">
                    <span className="material-symbols-outlined me-2 text-primary">dns</span>
                    EDR Provider
                  </Form.Label>
                  <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                      className="report-dropdown-toggle w-100 text-start"
                      required
                    >
                      <span className="text-truncate">
                        {selectedProvider?.name || 'Select an EDR provider'}
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="report-dropdown-menu w-100">
                      {edrProviders.map((provider) => (
                        <button
                          key={provider.id}
                          type="button"
                          className={`report-dropdown-item ${formData.edr_provider === provider.id ? 'selected' : ''}`}
                          onClick={() => handleInputChange({ target: { name: 'edr_provider', value: provider.id } })}
                        >
                          <span className="report-dropdown-icon">
                            {formData.edr_provider === provider.id ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                          </span>
                          <span>{provider.name} ({provider.provider})</span>
                        </button>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Form.Text className="text-muted">
                    Choose your security data source for report generation
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <span className="material-symbols-outlined me-2 text-primary">account_tree</span>
                        Account
                      </Form.Label>
                      <Dropdown autoClose="outside">
                        <Dropdown.Toggle
                          className="report-dropdown-toggle w-100 text-start"
                          disabled={!formData.edr_provider}
                        >
                          <span className="text-truncate">
                            {selectedAccount?.name || 'All Accounts'}
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="report-dropdown-menu w-100">
                          <button
                            type="button"
                            className={`report-dropdown-item ${!formData.account_id ? 'selected' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'account_id', value: '' } })}
                          >
                            <span className="report-dropdown-icon">
                              {!formData.account_id ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>All Accounts</span>
                          </button>
                          {hierarchySlices.accounts.map((account) => (
                            <button
                              key={account.id}
                              type="button"
                              className={`report-dropdown-item ${formData.account_id === account.id ? 'selected' : ''}`}
                              onClick={() => handleInputChange({ target: { name: 'account_id', value: account.id } })}
                            >
                              <span className="report-dropdown-icon">
                                {formData.account_id === account.id ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                              </span>
                              <span>{account.name}</span>
                            </button>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <Form.Text className="text-muted">
                        Select account to scope the report
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <span className="material-symbols-outlined me-2 text-primary">location_on</span>
                        Sites
                        {selectedSiteIds.length > 0 && (
                          <span className="badge bg-primary ms-2">{selectedSiteIds.length}</span>
                        )}
                      </Form.Label>
                      <Dropdown autoClose="outside">
                        <Dropdown.Toggle
                          className="report-dropdown-toggle w-100 text-start"
                          disabled={!formData.account_id || hierarchySlices.sites.length === 0}
                        >
                          <span className="text-truncate">
                            {selectedSiteNames.length === 0 ? 'All Sites' : selectedSiteNames.join(', ')}
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="report-dropdown-menu w-100">
                          <button
                            type="button"
                            className={`report-dropdown-item ${selectedSiteIds.length === 0 ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedSiteIds([]);
                              setSelectedGroupIds([]);
                            }}
                          >
                            <span className="report-dropdown-icon">
                              {selectedSiteIds.length === 0 ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>All Sites</span>
                          </button>
                          {hierarchySlices.sites.map((site) => (
                            <button
                              key={site.id}
                              type="button"
                              className={`report-dropdown-item ${selectedSiteIds.includes(site.id) ? 'selected' : ''}`}
                              onClick={() => toggleSiteSelection(site.id)}
                            >
                              <span className="report-dropdown-icon">
                                {selectedSiteIds.includes(site.id) ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                              </span>
                              <span>{site.name}</span>
                            </button>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <Form.Text className="text-muted">
                        Select one or more sites for detailed reporting
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <span className="material-symbols-outlined me-2 text-primary">groups</span>
                        Groups
                        {selectedGroupIds.length > 0 && (
                          <span className="badge bg-info ms-2">{selectedGroupIds.length}</span>
                        )}
                      </Form.Label>
                      <Dropdown autoClose="outside">
                        <Dropdown.Toggle
                          className="report-dropdown-toggle w-100 text-start"
                          disabled={!formData.account_id || hierarchySlices.groups.length === 0}
                        >
                          <span className="text-truncate">
                            {selectedGroupNames.length === 0 ? 'All Groups' : selectedGroupNames.join(', ')}
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="report-dropdown-menu w-100">
                          <button
                            type="button"
                            className={`report-dropdown-item ${selectedGroupIds.length === 0 ? 'selected' : ''}`}
                            onClick={() => setSelectedGroupIds([])}
                          >
                            <span className="report-dropdown-icon">
                              {selectedGroupIds.length === 0 ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>All Groups</span>
                          </button>
                          {hierarchySlices.groups.map((group) => (
                            <button
                              key={group.id}
                              type="button"
                              className={`report-dropdown-item ${selectedGroupIds.includes(group.id) ? 'selected' : ''}`}
                              onClick={() => toggleGroupSelection(group.id)}
                            >
                              <span className="report-dropdown-icon">
                                {selectedGroupIds.includes(group.id) ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                              </span>
                              <span>{group.name}</span>
                            </button>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <Form.Text className="text-muted">
                        Groups follow selected sites
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label className="d-flex align-items-center">
                        <span className="material-symbols-outlined me-2 text-primary">schedule</span>
                        Reporting Period
                      </Form.Label>
                      <Dropdown autoClose="outside">
                        <Dropdown.Toggle
                          className="report-dropdown-toggle w-100 text-start"
                        >
                          <span className="text-truncate">
                            {formData.period === 'hourly' ? 'Hourly (Last 60 Minutes)' :
                             formData.period === 'daily' ? 'Daily (Last 24 Hours)' :
                             formData.period === 'weekly' ? 'Weekly (Last 7 Days)' :
                             formData.period === 'monthly' ? 'Monthly (Last 30 Days)' :
                             formData.period === 'yearly' ? 'Yearly (Last 365 Days)' :
                             formData.period === 'all_time' ? 'All Time' : 'Daily (Last 24 Hours)'}
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="report-dropdown-menu w-100">
                          <button
                            type="button"
                            className={`report-dropdown-item ${formData.period === 'hourly' ? 'selected' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'period', value: 'hourly' } })}
                          >
                            <span className="report-dropdown-icon">
                              {formData.period === 'hourly' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>Hourly (Last 60 Minutes)</span>
                          </button>
                          <button
                            type="button"
                            className={`report-dropdown-item ${formData.period === 'daily' ? 'selected' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'period', value: 'daily' } })}
                          >
                            <span className="report-dropdown-icon">
                              {formData.period === 'daily' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>Daily (Last 24 Hours)</span>
                          </button>
                          <button
                            type="button"
                            className={`report-dropdown-item ${formData.period === 'weekly' ? 'selected' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'period', value: 'weekly' } })}
                          >
                            <span className="report-dropdown-icon">
                              {formData.period === 'weekly' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>Weekly (Last 7 Days)</span>
                          </button>
                          <button
                            type="button"
                            className={`report-dropdown-item ${formData.period === 'monthly' ? 'selected' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'period', value: 'monthly' } })}
                          >
                            <span className="report-dropdown-icon">
                              {formData.period === 'monthly' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>Monthly (Last 30 Days)</span>
                          </button>
                          <button
                            type="button"
                            className={`report-dropdown-item ${formData.period === 'yearly' ? 'selected' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'period', value: 'yearly' } })}
                          >
                            <span className="report-dropdown-icon">
                              {formData.period === 'yearly' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>Yearly (Last 365 Days)</span>
                          </button>
                          <button
                            type="button"
                            className={`report-dropdown-item ${formData.period === 'all_time' ? 'selected' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'period', value: 'all_time' } })}
                          >
                            <span className="report-dropdown-icon">
                              {formData.period === 'all_time' ? <FaCheckSquare className="text-success" /> : <FaRegSquare className="text-muted" />}
                            </span>
                            <span>All Time</span>
                          </button>
                        </Dropdown.Menu>
                      </Dropdown>
                      <Form.Text className="text-muted">
                        Select the time range for log analysis and chart granularity
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <span className="material-symbols-outlined me-2 text-primary">event</span>
                        Start Date
                      </Form.Label>
                      <div className="date-picker-wrapper">
                        <Form.Control
                          type="date"
                          name="start_date"
                          value={formData.start_date}
                          onChange={handleInputChange}
                          className="date-picker-input"
                        />
                      </div>
                      <Form.Text className="text-muted">
                        Optional: Override relative period with exact date range
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <span className="material-symbols-outlined me-2 text-primary">event_available</span>
                        End Date
                      </Form.Label>
                      <div className="date-picker-wrapper">
                        <Form.Control
                          type="date"
                          name="end_date"
                          value={formData.end_date}
                          onChange={handleInputChange}
                          className="date-picker-input"
                        />
                      </div>
                      <Form.Text className="text-muted">
                        Leave empty to use selected reporting period
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex gap-3 mt-4">
                  <Button
                    type="button"
                    variant="primary"
                    className="d-flex align-items-center px-4"
                    onClick={handleFetchScopeData}
                    disabled={!formData.edr_provider || !formData.account_id || fetchingScopeData}
                  >
                    {fetchingScopeData ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Fetching Scope Data...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined me-2">search</span>
                        Fetch Scope Data
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-primary"
                    className="d-flex align-items-center px-4"
                    onClick={() => setShowPreviewModal(true)}
                    disabled={fetchingScopeData}
                  >
                    <span className="material-symbols-outlined me-2">visibility</span>
                    Preview
                  </Button>
                  <Button
                    type="button"
                    variant="success"
                    className="d-flex align-items-center px-4"
                    disabled={generating || fetchingScopeData}
                    onClick={handleOpenGenerateModal}
                  >
                    {generating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined me-2">rocket_launch</span>
                        Generate Report
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mt-4">
          {error}
        </Alert>
      )}

      {fetchingScopeData && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm text-primary me-2" />
              <h5 className="mb-0">Fetching Scope Data</h5>
            </div>
          </Card.Header>
          <Card.Body className="text-center py-4">
            <div className="spinner-border text-primary mb-3" />
            <div className="fw-bold">Fetching scoped report data...</div>
            <div className="text-muted small">
              Loading incidents, endpoints, and report sections for the selected account, sites, groups, and date range.
            </div>
          </Card.Body>
        </Card>
      )}

      {warmingScopeData && !fetchingScopeData && (
        <Card className="mb-4 border-info bg-light">
          <Card.Header className="bg-white border-bottom border-info">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm text-info me-2" />
              <h5 className="mb-0">Refreshing Native Insights</h5>
            </div>
          </Card.Header>
          <Card.Body className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-info mb-2" />
            <div className="fw-bold">Refreshing native insights automatically...</div>
            <div className="text-muted small">
              Scoped data is already loaded. Native Threats, Applications, and XDR sections will appear as soon as warmup finishes.
            </div>
          </Card.Body>
        </Card>
      )}

      {fetchedScope && !hasFetchedCurrentScope && !fetchingScopeData && (
        <Alert variant="warning" className="mb-4">
          Scope filters changed. Click `Fetch Scope Data` to load the current scope before viewing sections 1.1 to 1.5.
        </Alert>
      )}

      {hasFetchedCurrentScope && siteSections.length > 1 && (
        <Accordion defaultActiveKey={siteSections.map((section) => section.site.id)} alwaysOpen className="mt-4">
          {siteSections.map((section) => {
            const pageKey = section.site.id;
            const perPage = 8;
            const currentPage = incidentPages[pageKey] || 1;
            const totalPages = Math.max(1, Math.ceil(section.incidentDetails.length / perPage));
            const pagedIncidents = section.incidentDetails.slice((currentPage - 1) * perPage, currentPage * perPage);
            const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).filter((n) => totalPages <= 7 || n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1);
            return (
                <Accordion.Item eventKey={section.site.id} key={section.site.id}>
                <Accordion.Header>{section.site.name}</Accordion.Header>
                <Accordion.Body>
                  <div className="text-muted mb-2">Showing fetched scope for {selectedProvider?.name || 'selected provider'}{selectedAccount ? ` / ${selectedAccount.name}` : ''}{` / Site: ${section.site.name}`}{section.siteGroups.length > 0 ? ` / Groups: ${section.siteGroups.map((group) => group.name).join(', ')}` : ' / All Groups'}{` / Period: ${getScopePeriodLabel(effectiveFetchedScope)}`}</div>
                  <div className="small text-muted mb-3">
                    <strong>Native scope:</strong> {normalizeNativeScopeText(section.reportScope) || 'Unavailable'}
                    {' '}|{' '}
                    <strong>Site ID:</strong> {section.site.id}
                  </div>
                  <Card className="mt-2"><Card.Header><FaExclamationTriangle className="me-2" />1.1 Incident Detail</Card.Header><Card.Body>
                    <Card><Card.Header>1.1.1 Incident Detection Details</Card.Header><Card.Body>
                      <div className="small text-muted mb-3">
                        Total alerts: <strong>{summaryAlertCount}</strong>
                        {' '}|{' '}
                        Total endpoints: <strong>{summaryEndpointCount}</strong>
                      </div>
                      <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Status</th><th>Threat Details</th><th>Confidence Level</th><th>Endpoint</th><th>Last Reported IP</th><th>Verdict</th><th>Classification</th><th>Path</th></tr></thead><tbody>{section.incidentDetails.length === 0 ? <tr><td colSpan="8" className="text-center py-4"><div className="fw-bold mb-1">No incidents found for this site</div><div className="small text-muted mb-2">{section.site.name} did not return any incidents in the current report period.</div><div className="small">Endpoints still found in scope: <strong>{section.siteEndpoints.length}</strong></div></td></tr> : pagedIncidents.map((incident) => <tr key={incident.id}><td>{incident.status}</td><td>{incident.threatDetails}</td><td>{incident.confidenceLevel}</td><td>{incident.endpoint}</td><td>{incident.lastReportedIP}</td><td>{incident.analystVerdict}</td><td>{incident.classification}</td><td><small>{incident.path}</small></td></tr>)}</tbody></table></div>
                      {section.incidentDetails.length > perPage && <div className="d-flex justify-content-end align-items-center gap-2 mt-3"><Button variant="outline-secondary" size="sm" disabled={currentPage === 1} onClick={() => setIncidentPages((prev) => ({ ...prev, [pageKey]: Math.max(1, currentPage - 1) }))}>Prev</Button>{pageNumbers.map((n, index) => { const prevPage = pageNumbers[index - 1]; const showGap = prevPage && n - prevPage > 1; return <React.Fragment key={n}>{showGap && <span className="small text-muted px-1">...</span>}<Button variant={n === currentPage ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setIncidentPages((prev) => ({ ...prev, [pageKey]: n }))}>{n}</Button></React.Fragment>; })}<Button variant="outline-secondary" size="sm" disabled={currentPage === totalPages} onClick={() => setIncidentPages((prev) => ({ ...prev, [pageKey]: Math.min(totalPages, currentPage + 1) }))}>Next</Button></div>}
                    </Card.Body></Card>
                    <Row className="g-4 mt-1"><Col lg={6}><Card className="h-100"><Card.Header><FaServer className="me-2" />1.1.2 Top Devices At Risk</Card.Header><Card.Body>{section.topDevicesAtRisk.length === 0 ? <p className="text-muted mb-0">No risky devices in this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Name</th><th>Last Reported IP</th><th>Threats</th><th>Unique Threats</th><th>Policy Mode</th></tr></thead><tbody>{section.topDevicesAtRisk.map((device) => <tr key={device.name}><td>{device.name}</td><td>{device.lastReportedIP}</td><td>{device.threats}</td><td>{device.uniqueThreats}</td><td>{device.policyMode}</td></tr>)}</tbody></table></div>}</Card.Body></Card></Col><Col lg={6}><Card className="h-100"><Card.Header><FaSearch className="me-2" />1.1.3 Top Indicators</Card.Header><Card.Body>{section.topIndicators.length === 0 ? <p className="text-muted mb-0">No indicators found in this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Indicator</th><th>Occurrences</th><th>Most Seen Agent</th><th>Last Reported IP</th></tr></thead><tbody>{section.topIndicators.map((indicator) => <tr key={indicator.label}><td>{indicator.label.replaceAll('_', ' ')}</td><td>{indicator.occurrences}</td><td>{indicator.mostSeenAgent}</td><td>{indicator.lastReportedIP}</td></tr>)}</tbody></table></div>}</Card.Body></Card></Col></Row>
                  </Card.Body></Card>
                  <Card className="mt-4"><Card.Header>1.2 Enterprise Risk Profile Detail</Card.Header><Card.Body><Card><Card.Header>1.2.1 Enterprise Risk Profile Detail</Card.Header><Card.Body>{section.enterpriseRiskProfileDetails.length === 0 ? <p className="text-muted mb-0">No enterprise risk profile detail found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Endpoint</th><th>Last Reported IP</th><th>Path / File Name</th></tr></thead><tbody>{section.enterpriseRiskProfileDetails.map((item) => <tr key={`${section.site.id}-${item.endpoint}-${item.pathOrFileName}`}><td>{item.endpoint}</td><td>{item.lastReportedIP}</td><td>{item.pathOrFileName}</td></tr>)}</tbody></table></div>}</Card.Body></Card></Card.Body></Card>
                  <Card className="mt-4"><Card.Header>1.3 Application Vulnerability Detail</Card.Header><Card.Body><Card className="mb-4"><Card.Header>1.3.1 Application Vulnerability Details</Card.Header><Card.Body>{section.applicationVulnerabilityDetails.length === 0 ? <p className="text-muted mb-0">No Windows application observations found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Application Name</th><th>Vendor</th><th>Highest Severity</th><th>Highest NVD Base Score</th><th>Number of CVEs</th><th>Number of Endpoints</th><th>Application Detection Date</th><th>Days from Detection</th></tr></thead><tbody>{section.applicationVulnerabilityDetails.map((item) => <tr key={`${section.site.id}-${item.id}`}><td>{item.applicationName}</td><td>{item.vendor}</td><td>{item.highestSeverity}</td><td>{item.highestNvdBaseScore}</td><td>{item.numberOfCVEs}</td><td>{item.numberOfEndpoints}</td><td>{new Date(item.applicationDetectionDate).toLocaleString()}</td><td>{item.daysFromDetection}</td></tr>)}</tbody></table></div>}</Card.Body></Card><Card className="mb-4"><Card.Header>1.3.2 Top Windows Applications of All Time</Card.Header><Card.Body>{section.topWindowsApplications.length === 0 ? <p className="text-muted mb-0">No Windows application telemetry found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size</th></tr></thead><tbody>{section.topWindowsApplications.map((app, index) => <tr key={getApplicationRowKey(`${section.site.id}-windows-all`, app, index)}><td>{app.label || app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{app.size || '-'}</td></tr>)}</tbody></table></div>}</Card.Body></Card><Card className="mb-4"><Card.Header>1.3.3 Top New / Updated Windows Applications During Report Period</Card.Header><Card.Body>{section.topNewUpdatedWindowsApplications.length === 0 ? <p className="text-muted mb-0">No recently observed Windows applications found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size / Last Observed</th></tr></thead><tbody>{section.topNewUpdatedWindowsApplications.map((app, index) => <tr key={getApplicationRowKey(`${section.site.id}-windows-period`, app, index)}><td>{app.label || app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{app.size || (app.lastSeen ? new Date(app.lastSeen).toLocaleString() : app.lastObserved || '-')}</td></tr>)}</tbody></table></div>}</Card.Body></Card><Card className="mb-4"><Card.Header>1.3.4 Top MacOS Applications of All Time</Card.Header><Card.Body>{section.topMacOSApplications.length === 0 ? <p className="text-muted mb-0">No macOS application telemetry found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size</th></tr></thead><tbody>{section.topMacOSApplications.map((app, index) => <tr key={getApplicationRowKey(`${section.site.id}-mac-all`, app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{app.size || '-'}</td></tr>)}</tbody></table></div>}</Card.Body></Card><Card className="mb-4"><Card.Header>1.3.5 Top New / Updated MacOS Applications During Report Period</Card.Header><Card.Body>{section.topNewUpdatedMacOSApplications.length === 0 ? <p className="text-muted mb-0">No recently observed macOS applications found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size / Last Observed</th></tr></thead><tbody>{section.topNewUpdatedMacOSApplications.map((app, index) => <tr key={getApplicationRowKey(`${section.site.id}-mac-period`, app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{getApplicationPeriodAuxValue(app)}</td></tr>)}</tbody></table></div>}</Card.Body></Card><Card className="mb-4"><Card.Header>1.3.6 Top Linux Applications of All Time</Card.Header><Card.Body>{section.topLinuxApplications.length === 0 ? <p className="text-muted mb-0">No Linux application telemetry found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size</th></tr></thead><tbody>{section.topLinuxApplications.map((app, index) => <tr key={getApplicationRowKey(`${section.site.id}-linux-all`, app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{app.size || '-'}</td></tr>)}</tbody></table></div>}</Card.Body></Card><Card><Card.Header>1.3.7 Top New / Updated Linux Applications During Report Period</Card.Header><Card.Body>{section.topNewUpdatedLinuxApplications.length === 0 ? <p className="text-muted mb-0">No recently observed Linux applications found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size / Last Observed</th></tr></thead><tbody>{section.topNewUpdatedLinuxApplications.map((app, index) => <tr key={getApplicationRowKey(`${section.site.id}-linux-period`, app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{getApplicationPeriodAuxValue(app)}</td></tr>)}</tbody></table></div>}</Card.Body></Card></Card.Body></Card>
                  <Card className="mt-4"><Card.Header>1.4 SentinelOne Agent Health & Coverage</Card.Header><Card.Body><Card className="mb-4"><Card.Header>1.4.1 SentinelOne Asset View</Card.Header><Card.Body>{section.sentinelOneAssetView.length === 0 ? <p className="text-muted mb-0">No asset view data found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Platform</th><th>Total</th><th>Laptops</th><th>Desktops</th><th>Servers</th></tr></thead><tbody>{section.sentinelOneAssetView.map((item) => <tr key={`${section.site.id}-${item.platform}`}><td>{item.platform}</td><td>{item.total}</td><td>{item.laptops}</td><td>{item.desktops}</td><td>{item.servers}</td></tr>)}</tbody></table></div>}</Card.Body></Card><Card><Card.Header>1.4.2 SentinelOne Change Management</Card.Header><Card.Body>{section.sentinelOneChangeManagement.length === 0 ? <p className="text-muted mb-0">No change management data found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Event Type</th><th>Count</th><th>Notes</th></tr></thead><tbody>{section.sentinelOneChangeManagement.map((item) => <tr key={`${section.site.id}-${item.eventType}`}><td>{item.eventType}</td><td>{item.count}</td><td>{item.notes}</td></tr>)}</tbody></table></div>}</Card.Body></Card></Card.Body></Card>
                  <Card className="mt-4"><Card.Header>1.5 XDR</Card.Header><Card.Body><Card><Card.Header>1.5.1 Critical Network Services Traffic Overview - Palo Alto FW</Card.Header><Card.Body>{section.xdrTopProtocolServiceUsages.length === 0 ? <p className="text-muted mb-0">No XDR or firewall traffic overview data found for this site.</p> : <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Protocol / Service</th><th>Usage (%)</th><th>Security Assessment & Recommendation</th></tr></thead><tbody>{section.xdrTopProtocolServiceUsages.map((item, index) => <tr key={`${section.site.id}-${item.protocolService}-${index}`}><td>{item.protocolService}</td><td>{typeof item.usagePercentage === 'number' ? `${item.usagePercentage.toFixed(2)}%` : item.usagePercentage}</td><td>{item.securityAssessmentRecommendation}</td></tr>)}</tbody></table></div>}</Card.Body></Card></Card.Body></Card>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {hasFetchedCurrentScope && siteSections.length <= 1 && (
      <Card className="mt-4">
        <Card.Header>
          <FaExclamationTriangle className="me-2" />
          1.1 Incident Detail
        </Card.Header>
        <Card.Body>
          <div className="text-muted mb-2">
            Showing fetched scope for {selectedProvider?.name || 'selected provider'}
            {selectedAccount ? ` / ${selectedAccount.name}` : ''}
            {fetchedSites.length > 0 ? ` / Sites: ${fetchedSites.map((site) => site.name).join(', ')}` : ' / All Sites'}
            {fetchedGroups.length > 0 ? ` / Groups: ${fetchedGroups.map((group) => group.name).join(', ')}` : ' / All Groups'}
            {` / Period: ${getScopePeriodLabel(effectiveFetchedScope)}`}
          </div>
          <div className="small text-muted mb-3">
            <strong>Native scope:</strong> {normalizeNativeScopeText(siteSections[0]?.reportScope || (fetchedSites[0] ? getSiteNativeData(reportData, fetchedSites[0].id)?.report_scope : '')) || 'Unavailable'}
            {fetchedSites[0]?.id ? (
              <>
                {' '}|{' '}
                <strong>Site ID:</strong> {fetchedSites[0].id}
              </>
            ) : null}
          </div>
          <Row className="g-4">
            <Col lg={12}>
              <Card className="h-100">
                <Card.Header>1.1.1 Incident Detection Details</Card.Header>
                <Card.Body>
                  <div className="small text-muted mb-3">
                    Total alerts: <strong>{summaryAlertCount}</strong>
                    {' '}|{' '}
                    Total endpoints: <strong>{summaryEndpointCount}</strong>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Threat Details</th>
                          <th>Confidence Level</th>
                          <th>Endpoint</th>
                          <th>Last Reported IP</th>
                          <th>Verdict</th>
                          <th>Classification</th>
                          <th>Path</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incidentDetails.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <div className="fw-bold mb-1">No incidents found for this scope</div>
                              <div className="small text-muted mb-2">
                                {scopeSummary || 'The selected scope'} did not return any incidents in the current report period.
                              </div>
                              <div className="small">
                                Endpoints still found in scope: <strong>{scopedEndpoints.length}</strong>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          pagedIncidentDetails.map((incident) => (
                            <tr key={incident.id}>
                              <td>{incident.status}</td>
                              <td>{incident.threatDetails}</td>
                              <td>{incident.confidenceLevel}</td>
                              <td>{incident.endpoint}</td>
                              <td>{incident.lastReportedIP}</td>
                              <td>{incident.analystVerdict}</td>
                              <td>{incident.classification}</td>
                              <td><small>{incident.path}</small></td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {incidentDetails.length > incidentsPerPage && (
                    <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={incidentPage === 1}
                        onClick={() => setIncidentPage((page) => Math.max(1, page - 1))}
                      >
                        Prev
                      </Button>
                      {visibleIncidentPages.map((pageNumber, index) => {
                        const previousPage = visibleIncidentPages[index - 1];
                        const showGap = previousPage && pageNumber - previousPage > 1;

                        return (
                          <React.Fragment key={pageNumber}>
                            {showGap && <span className="small text-muted px-1">...</span>}
                            <Button
                              variant={pageNumber === incidentPage ? 'primary' : 'outline-secondary'}
                              size="sm"
                              onClick={() => setIncidentPage(pageNumber)}
                            >
                              {pageNumber}
                            </Button>
                          </React.Fragment>
                        );
                      })}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={incidentPage === totalIncidentPages}
                        onClick={() => setIncidentPage((page) => Math.min(totalIncidentPages, page + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="g-4 mt-1">
            <Col lg={6}>
              <Card className="h-100">
                <Card.Header>
                  <FaServer className="me-2" />
                  1.1.2 Top Devices At Risk
                </Card.Header>
                <Card.Body>
                  {topDevicesAtRisk.length === 0 ? (
                    <p className="text-muted mb-0">No risky devices in this scope.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Last Reported IP</th>
                            <th>Threats</th>
                            <th>Unique Threats</th>
                            <th>Policy Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topDevicesAtRisk.map((device) => (
                            <tr key={device.name}>
                              <td>{device.name}</td>
                              <td>{device.lastReportedIP}</td>
                              <td>{device.threats}</td>
                              <td>{device.uniqueThreats}</td>
                              <td>{device.policyMode}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="h-100">
                <Card.Header>
                  <FaSearch className="me-2" />
                  1.1.3 Top Indicators
                </Card.Header>
                <Card.Body>
                  {topIndicators.length === 0 ? (
                    <p className="text-muted mb-0">No indicators found in this scope.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Indicator</th>
                            <th>Occurrences</th>
                            <th>Most Seen Agent</th>
                            <th>Last Reported IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topIndicators.map((indicator) => (
                            <tr key={indicator.label}>
                              <td>{indicator.label.replaceAll('_', ' ')}</td>
                              <td>{indicator.occurrences}</td>
                              <td>{indicator.mostSeenAgent}</td>
                              <td>{indicator.lastReportedIP}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      )}

      {hasFetchedCurrentScope && siteSections.length <= 1 && (
      <Card className="mt-4">
        <Card.Header>1.2 Enterprise Risk Profile Detail</Card.Header>
        <Card.Body>
          <Card>
            <Card.Header>1.2.1 Enterprise Risk Profile Detail</Card.Header>
            <Card.Body>
              {enterpriseRiskProfileDetails.length === 0 ? (
                <p className="text-muted mb-0">No enterprise risk profile detail found in this scope.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Endpoint</th>
                        <th>Last Reported IP</th>
                        <th>Path / File Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enterpriseRiskProfileDetails.map((item) => (
                        <tr key={`${item.endpoint}-${item.pathOrFileName}`}>
                          <td>{item.endpoint}</td>
                          <td>{item.lastReportedIP}</td>
                          <td>{item.pathOrFileName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
      )}

      {hasFetchedCurrentScope && siteSections.length <= 1 && (
      <Card className="mt-4">
        <Card.Header>1.3 Application Vulnerability Detail</Card.Header>
        <Card.Body>
          <Card className="mb-4">
            <Card.Header>1.3.1 Application Vulnerability Details</Card.Header>
            <Card.Body>
              {applicationVulnerabilityDetails.length === 0 ? (
                <p className="text-muted mb-0">No Windows application observations found in this scope.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Application Name</th>
                        <th>Vendor</th>
                        <th>Highest Severity</th>
                        <th>Highest NVD Base Score</th>
                        <th>Number of CVEs</th>
                        <th>Number of Endpoints</th>
                        <th>Application Detection Date</th>
                        <th>Days from Detection</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applicationVulnerabilityDetails.map((item) => (
                        <tr key={item.id}>
                          <td>{item.applicationName}</td>
                          <td>{item.vendor}</td>
                          <td>{item.highestSeverity}</td>
                          <td>{item.highestNvdBaseScore}</td>
                          <td>{item.numberOfCVEs}</td>
                          <td>{item.numberOfEndpoints}</td>
                          <td>{new Date(item.applicationDetectionDate).toLocaleString()}</td>
                          <td>{item.daysFromDetection}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>1.3.2 Top Windows Applications of All Time</Card.Header>
            <Card.Body>
              {resolvedTopWindowsApplications.length === 0 ? (
                <p className="text-muted mb-0">No Windows application telemetry found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Publisher</th>
                        <th>Version</th>
                        <th>Count</th>
                        <th>Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resolvedTopWindowsApplications.map((app, index) => (
                        <tr key={getApplicationRowKey('single-windows-all', app, index)}>
                          <td>{app.label || app.name}</td>
                          <td>{app.publisher}</td>
                          <td>{app.version}</td>
                          <td>{app.count}</td>
                          <td>{app.size || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>1.3.3 Top New / Updated Windows Applications During Report Period</Card.Header>
            <Card.Body>
              {resolvedTopNewUpdatedWindowsApplications.length === 0 ? (
                <p className="text-muted mb-0">No recently observed Windows applications found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Publisher</th>
                        <th>Version</th>
                        <th>Count</th>
                        <th>Size / Last Observed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resolvedTopNewUpdatedWindowsApplications.map((app, index) => (
                        <tr key={getApplicationRowKey('single-windows-period', app, index)}>
                          <td>{app.label || app.name}</td>
                          <td>{app.publisher}</td>
                          <td>{app.version}</td>
                          <td>{app.count}</td>
                          <td>{app.size || (app.lastSeen ? new Date(app.lastSeen).toLocaleString() : app.lastObserved || '-')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>1.3.4 Top MacOS Applications of All Time</Card.Header>
            <Card.Body>
              {topMacOSApplications.length === 0 ? (
                <p className="text-muted mb-0">No macOS application telemetry found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size</th></tr></thead>
                    <tbody>{topMacOSApplications.map((app, index) => <tr key={getApplicationRowKey('single-mac-all', app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{app.size || '-'}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>1.3.5 Top New / Updated MacOS Applications During Report Period</Card.Header>
            <Card.Body>
              {topNewUpdatedMacOSApplications.length === 0 ? (
                <p className="text-muted mb-0">No recently observed macOS applications found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size / Last Observed</th></tr></thead>
                    <tbody>{topNewUpdatedMacOSApplications.map((app, index) => <tr key={getApplicationRowKey('single-mac-period', app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{getApplicationPeriodAuxValue(app)}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>1.3.6 Top Linux Applications of All Time</Card.Header>
            <Card.Body>
              {topLinuxApplications.length === 0 ? (
                <p className="text-muted mb-0">No Linux application telemetry found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size</th></tr></thead>
                    <tbody>{topLinuxApplications.map((app, index) => <tr key={getApplicationRowKey('single-linux-all', app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{app.size || '-'}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>1.3.7 Top New / Updated Linux Applications During Report Period</Card.Header>
            <Card.Body>
              {topNewUpdatedLinuxApplications.length === 0 ? (
                <p className="text-muted mb-0">No recently observed Linux applications found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Name</th><th>Publisher</th><th>Version</th><th>Count</th><th>Size / Last Observed</th></tr></thead>
                    <tbody>{topNewUpdatedLinuxApplications.map((app, index) => <tr key={getApplicationRowKey('single-linux-period', app, index)}><td>{app.name}</td><td>{app.publisher}</td><td>{app.version}</td><td>{app.count}</td><td>{getApplicationPeriodAuxValue(app)}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
      )}

      {hasFetchedCurrentScope && siteSections.length <= 1 && (
      <Card className="mt-4">
        <Card.Header>1.4 SentinelOne Agent Health & Coverage</Card.Header>
        <Card.Body>
          <Card className="mb-4">
            <Card.Header>1.4.1 SentinelOne Asset View</Card.Header>
            <Card.Body>
              {sentinelOneAssetView.length === 0 ? (
                <p className="text-muted mb-0">No asset view data found in this scope.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Platform</th>
                        <th>Total</th>
                        <th>Laptops</th>
                        <th>Desktops</th>
                        <th>Servers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sentinelOneAssetView.map((item) => (
                        <tr key={item.platform}>
                          <td>{item.platform}</td>
                          <td>{item.total}</td>
                          <td>{item.laptops}</td>
                          <td>{item.desktops}</td>
                          <td>{item.servers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>1.4.2 SentinelOne Change Management</Card.Header>
            <Card.Body>
              {sentinelOneChangeManagement.length === 0 ? (
                <p className="text-muted mb-0">No change management data found in this scope.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Event Type</th>
                        <th>Count</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sentinelOneChangeManagement.map((item) => (
                        <tr key={item.eventType}>
                          <td>{item.eventType}</td>
                          <td>{item.count}</td>
                          <td>{item.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
      )}

      {hasFetchedCurrentScope && siteSections.length <= 1 && (
      <Card className="mt-4">
        <Card.Header>1.5 XDR</Card.Header>
        <Card.Body>
          <Card>
            <Card.Header>1.5.1 Critical Network Services Traffic Overview - Palo Alto FW</Card.Header>
            <Card.Body>
              {xdrTopProtocolServiceUsages.length === 0 ? (
                <p className="text-muted mb-0">No XDR or firewall traffic overview data found in this scope.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Protocol / Service</th>
                        <th>Usage (%)</th>
                        <th>Security Assessment & Recommendation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xdrTopProtocolServiceUsages.map((item, index) => (
                        <tr key={`${item.protocolService}-${index}`}>
                          <td>{item.protocolService}</td>
                          <td>{typeof item.usagePercentage === 'number' ? `${item.usagePercentage.toFixed(2)}%` : item.usagePercentage}</td>
                          <td>{item.securityAssessmentRecommendation}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
      )}

      {report && (
        <Card className="mt-4">
          <Card.Header>
            <FaDownload className="me-2" />
            Generated Report
          </Card.Header>
          <Card.Body>
            {report.message ? (
              <div>
                <Alert variant="success" className="mb-3">
                  <strong>Success!</strong> {report.message}
                </Alert>
                {report.summary && (
                  <Card bg="light" className="border-0">
                    <Card.Body className="py-3">
                      <div className="small text-muted mb-2">Generated from fetched scope data</div>
                      <div className="mb-2"><strong>Scope:</strong> {report.summary.scopeSummary}</div>
                      <div className="d-flex flex-wrap gap-4">
                        <div><strong>Format:</strong> {report.summary.format}</div>
                        <div><strong>Total alerts:</strong> {report.summary.totalAlerts}</div>
                        <div><strong>Total endpoints:</strong> {report.summary.totalEndpoints}</div>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </div>
            ) : (
              <div>
                <h5>Report Details</h5>
                <p><strong>ID:</strong> {report.id}</p>
                <p><strong>EDR Provider:</strong> {edrProviders.find((provider) => provider.id === report.edr_provider)?.name || selectedProvider?.name || report.edr_provider}</p>
                <p><strong>Language:</strong> {report.language}</p>
                <p><strong>Format:</strong> {report.format}</p>
                <p><strong>Created:</strong> {new Date(report.created_at).toLocaleString()}</p>
                
                <hr />
                
                <h6>Report Content:</h6>
                <div className="border p-3 bg-light">
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                    {report.content}
                  </pre>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Report Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="fw-bold">Provider Scope</div>
            <div className="text-muted">
              {selectedProvider ? selectedProvider.name : 'No provider selected'}
              {selectedAccount && ` / ${selectedAccount.name}`}
              {selectedSiteIds.length > 0 && ` / Sites: ${hierarchySlices.sites.filter((site) => selectedSiteIds.includes(site.id)).map((site) => site.name).join(', ')}`}
              {selectedGroupIds.length > 0 && ` / Groups: ${hierarchySlices.groups.filter((group) => selectedGroupIds.includes(group.id)).map((group) => group.name).join(', ')}`}
            </div>
          </div>

          <div className="mb-3">
            <div className="fw-bold">Fetched Scope Snapshot</div>
            <div className="text-muted">
              {hasFetchedCurrentScope
                ? [
                    selectedProvider?.name || 'Selected provider',
                    selectedAccount?.name || 'All Accounts',
                    fetchedSites.length > 0 ? `Sites: ${fetchedSites.map((site) => site.name).join(', ')}` : 'All Sites',
                    fetchedGroups.length > 0 ? `Groups: ${fetchedGroups.map((group) => group.name).join(', ')}` : 'All Groups',
                    `Period: ${getScopePeriodLabel(effectiveFetchedScope)}`,
                  ].join(' / ')
                : 'No fetched scope yet. Generate Report will only use a scoped template after you click Fetch Scope Data.'}
            </div>
          </div>

          <div className="mb-3">
            <div className="fw-bold">Filters</div>
            <div className="text-muted">
              Period: {formData.start_date && formData.end_date ? `${formData.start_date} to ${formData.end_date}` : formData.period}
            </div>
          </div>

          <div className="mb-3">
            <div className="fw-bold">Output</div>
            <div className="text-muted">Format: {getOutputFormatLabel(formData.format)}</div>
            <div className="text-muted">Language: {formData.language.toUpperCase()}</div>
            <div className="text-muted">Engine: {formData.engine}</div>
            <div className="text-muted">Template: {hasFetchedCurrentScope ? 'Fetched Scope Report' : 'Standard Report'}</div>
          </div>

          <div className="small text-muted">
            This preview shows the report scope and settings that will be sent when you generate the report.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-white">
          <div className="d-flex align-items-center">
            <span className="material-symbols-outlined me-2 text-primary">settings_suggest</span>
            <Modal.Title className="mb-0">Finalize Report Output</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label className="d-flex align-items-center">
                    <span className="material-symbols-outlined me-2 text-primary">memory</span>
                    Report Engine
                  </Form.Label>
                  <div className="border rounded p-3 bg-light">
                    <div className="d-flex gap-4">
                      <Form.Check
                        type="radio"
                        id="modal-engine-system"
                        label={
                          <div>
                            <div className="fw-semibold">System (Professional Template)</div>
                            <div className="text-muted small">Faster, reliable reports using industry-standard templates</div>
                          </div>
                        }
                        name="engine"
                        value="system"
                        checked={formData.engine === 'system'}
                        onChange={handleInputChange}
                      />
                      <Form.Check
                        type="radio"
                        id="modal-engine-gemini"
                        label={
                          <div>
                            <div className="fw-semibold d-flex align-items-center">
                              Gemini AI (Deep Insight)
                              {!aiSettings.gemini_configured && <span className="badge bg-warning ms-2">Setup Required</span>}
                            </div>
                            <div className="text-muted small">Deep AI-driven analysis of incident logs and trends</div>
                          </div>
                        }
                        name="engine"
                        value="gemini"
                        checked={formData.engine === 'gemini'}
                        disabled={!aiSettings.gemini_configured}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  {!aiSettings.gemini_configured && (
                    <Alert variant="warning" className="mt-2 mb-0">
                      <span className="material-symbols-outlined me-2">info</span>
                      Admin must save the Gemini API key first from <strong>Admin &gt; AI Settings</strong>.
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-4">
                  <Form.Label className="d-flex align-items-center">
                    <span className="material-symbols-outlined me-2 text-primary">description</span>
                    Report Template
                  </Form.Label>
                  <div className="border rounded p-3 bg-light">
                    <div className="d-flex align-items-center">
                      <span className={`material-symbols-outlined me-2 ${hasFetchedCurrentScope ? 'text-success' : 'text-muted'}`}>
                        {hasFetchedCurrentScope ? 'check_circle' : 'help'}
                      </span>
                      <div>
                        <div className="fw-semibold">
                          {hasFetchedCurrentScope ? 'Fetched Scope Report' : 'Standard Report'}
                        </div>
                        <div className="text-muted small">
                          {hasFetchedCurrentScope
                            ? 'Based on the scope you fetched from account, sites, groups, and date range'
                            : 'Fetch scope data first for customized reporting'}
                        </div>
                      </div>
                    </div>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Alert variant={hasFetchedCurrentScope ? 'success' : 'warning'} className="mb-3 border-0">
                  <div className="d-flex align-items-start">
                    <span className="material-symbols-outlined me-2 mt-1">
                      {hasFetchedCurrentScope ? 'check_circle' : 'info'}
                    </span>
                    <div className="flex-grow-1">
                      <div className="fw-semibold mb-1">
                        {hasFetchedCurrentScope ? 'Scope Data Ready' : 'Scope Data Required'}
                      </div>
                      {hasFetchedCurrentScope ? (
                        <div>
                          <div className="text-muted small mb-2">Report will be generated from:</div>
                          <div className="d-flex flex-wrap gap-2">
                            <span className="badge bg-light text-dark">
                              {selectedProvider?.name || 'Selected provider'}
                            </span>
                            <span className="badge bg-light text-dark">
                              {selectedAccount?.name || 'All Accounts'}
                            </span>
                            {fetchedSites.length > 0 && (
                              <span className="badge bg-primary text-white">
                                Sites: {fetchedSites.length}
                              </span>
                            )}
                            {fetchedGroups.length > 0 && (
                              <span className="badge bg-info text-white">
                                Groups: {fetchedGroups.length}
                              </span>
                            )}
                            <span className="badge bg-secondary text-white">
                              {getScopePeriodLabel(effectiveFetchedScope)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted small">
                          Fetch Scope Data first so the generated report matches the exact data shown on this page.
                        </div>
                      )}
                    </div>
                  </div>
                </Alert>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center">
                    <span className="material-symbols-outlined me-2 text-primary">language</span>
                    Language
                  </Form.Label>
                  <Form.Select
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    className="border-0 shadow-sm"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center">
                    <span className="material-symbols-outlined me-2 text-primary">file_download</span>
                    Output Format
                  </Form.Label>
                  <Form.Select
                    name="format"
                    value={formData.format}
                    onChange={handleInputChange}
                    className="border-0 shadow-sm"
                  >
                    <option value="json">JSON</option>
                    <option value="pdf">PDF</option>
                    <option value="docx">DOCX</option>
                    <option value="pdf_docx">PDF + DOCX</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Theme Color</Form.Label>
                  <Form.Control
                    type="color"
                    name="theme_color"
                    value={formData.theme_color}
                    onChange={handleInputChange}
                    title="Choose your branding color"
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Logo (Upload)</Form.Label>
                  <Form.Control
                    type="file"
                    name="logo_file"
                    accept="image/*"
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    {formData.logo_file ? `Selected: ${formData.logo_file.name}` : 'Select a logo image from your device.'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGenerateReport} disabled={generating}>
            {generating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Generating...
              </>
            ) : (
              <>
                <FaFileAlt className="me-2" />
                Generate Report
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
        </div>
      </div>
    </div>
  );
};

export default ReportGenerator;
